import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Send, Search, Users, Radio, Megaphone, Bot, Paperclip, Ticket, Loader2,
  Plus, Check, CheckCheck, Smile, Trash2, Edit3, Pin, Star, Reply, Forward,
  MessageSquare, Shield, ChevronRight, MessageCircle, CheckSquare,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const emojis = ['👍', '❤️', '😂', '😮', '😢', '🎉', '👏', '🔥'];

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString())
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('pt-BR');
  } catch { return ''; }
};

// ────────────────────────  SIDEBAR: LISTA DE CONVERSAS  ────────────────────────
// ────────────────────────  MENU DE AÇÕES POR CONVERSA (V4.68)  ────────────────────────
function ConvActionMenu({ conv, onArchive, onDelete }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const cid = String(conv?.id || '').slice(0, 8) || 'unknown';
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} className="absolute right-2 top-2 z-10">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition p-1 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-800"
        title="Ações da conversa"
        data-testid={`dnachat-conv-menu-${cid}`}
      >⋮</button>
      {open && (
        <div className="absolute right-0 top-6 w-40 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 text-xs" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 hover:bg-zinc-50 text-zinc-700"
            onClick={(e) => { e.stopPropagation(); setOpen(false); onArchive?.(conv); }}
            data-testid={`dnachat-conv-archive-${cid}`}
          >📁 Arquivar</button>
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 hover:bg-rose-50 text-rose-700"
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete?.(conv); }}
            data-testid={`dnachat-conv-delete-${cid}`}
          >🗑 Excluir conversa</button>
        </div>
      )}
    </div>
  );
}


function ConversationsSidebar({ me, tab, setTab, conversations, selected, onSelect, onNewChat, onNewGroup, onNewBroadcast, onOpenTickets, onOpenAI, onArchive, onDelete, filters, setFilters, classList, onClearFilters, selectionMode, setSelectionMode, selectedIds, setSelectedIds, onBulkArchive, onBulkRestore }) {
  const [waMenuOpen, setWaMenuOpen] = React.useState(false);
  const [advOpen, setAdvOpen] = React.useState(false);
  const waRef = React.useRef(null);
  const advRef = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (waRef.current && !waRef.current.contains(e.target)) setWaMenuOpen(false);
      if (advRef.current && !advRef.current.contains(e.target)) setAdvOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const activeFilterCount = (filters?.class_ids?.length || 0)
    + (filters?.guardian_type ? 1 : 0) + (filters?.channel ? 1 : 0) + (filters?.status ? 1 : 0);
  const searchActive = !!(filters?.search || '').trim();
  const toggleClassId = (id) => setFilters((f) => {
    const cur = new Set(f.class_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    return { ...f, class_ids: Array.from(cur) };
  });
  const isArchivedView = filters?.status === 'archived';
  const toggleSel = (cid) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });
  const filteredVisible = React.useMemo(() => (conversations || []).filter((c) => {
    if (tab === 'chats') return ['direct', 'group', 'community'].includes(c.type);
    if (tab === 'channels') return c.type === 'channel';
    if (tab === 'broadcast') return c.type === 'broadcast';
    return true;
  }), [conversations, tab]);
  const selectAllVisible = () => setSelectedIds(new Set(filteredVisible.map((c) => c.id)));
  const clearSelection = () => setSelectedIds(new Set());
  return (
    <div className="w-80 border-r border-zinc-200 flex flex-col bg-white" data-testid="dnachat-sidebar">
      <div className="p-3 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500">DNA Chat</div>
          <div className="text-sm font-semibold text-zinc-800">{me?.name}</div>
        </div>
        <div className="flex items-center gap-1 relative" ref={waRef}>
          <button
            type="button"
            className="text-[10px] font-semibold px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 inline-flex items-center gap-1"
            onClick={() => setWaMenuOpen((v) => !v)}
            title="Gestão WhatsApp Business"
            data-testid="dnachat-wa-menu-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp <ChevronRight className={`w-3 h-3 transition-transform ${waMenuOpen ? 'rotate-90' : ''}`} />
          </button>
          {waMenuOpen && (
            <div className="absolute right-0 top-8 z-30 w-56 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 text-sm" data-testid="dnachat-wa-menu">
              {[
                { href: '/v2/chat/whatsapp/config',    label: 'Configuração',   testid: 'wa-menu-config' },
                { href: '/v2/chat/whatsapp/queue',     label: 'Fila',           testid: 'wa-menu-queue' },
                { href: '/v2/chat/whatsapp/failures',  label: 'Falhas',         testid: 'wa-menu-failures' },
                { href: '/v2/chat/whatsapp/templates', label: 'Templates',      testid: 'wa-menu-templates' },
              ].map((l) => (
                <a key={l.href} href={l.href}
                  className="block px-3 py-2 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700"
                  data-testid={l.testid}>
                  {l.label}
                </a>
              ))}
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={onNewChat} title="Nova conversa" data-testid="dnachat-new-chat">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-5 border-b border-zinc-200 text-[10px]">
        {[
          { key: 'chats', icon: MessageSquare, label: 'Chats' },
          { key: 'channels', icon: Radio, label: 'Canais' },
          { key: 'broadcast', icon: Megaphone, label: 'Transm.' },
          { key: 'tickets', icon: Ticket, label: 'Chamados' },
          { key: 'ai', icon: Bot, label: 'IA' },
        ].map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            className={`py-2 flex flex-col items-center gap-1 border-b-2 transition ${tab === key ? 'border-indigo-600 text-indigo-700 bg-indigo-50' : 'border-transparent text-zinc-500 hover:bg-zinc-50'}`}
            onClick={() => { setTab(key); if (key === 'tickets') onOpenTickets(); if (key === 'ai') onOpenAI(); }}
            data-testid={`dnachat-tab-${key}`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {(tab === 'chats' || tab === 'channels' || tab === 'broadcast') && (
          <div>
            {/* V4.78 · Busca inteligente + filtro por turma + filtros avançados */}
            <div className="p-2 border-b border-zinc-100 bg-zinc-50/60 sticky top-0 z-10">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Buscar aluno, responsável, WhatsApp ou turma…"
                  className="w-full pl-7 pr-16 py-1.5 text-xs border border-zinc-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  value={filters?.search || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  data-testid="dnachat-search-input"
                />
                {(searchActive || activeFilterCount > 0) && (
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 hover:bg-zinc-300 text-zinc-700"
                    onClick={onClearFilters}
                    title="Limpar filtros"
                    data-testid="dnachat-clear-filters"
                  >Limpar</button>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1.5 text-[10px] relative" ref={advRef}>
                <button
                  type="button"
                  onClick={() => setAdvOpen((v) => !v)}
                  className={`px-2 py-0.5 rounded border ${(filters?.class_ids?.length || 0) > 0 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                  data-testid="dnachat-filter-class-btn"
                >Turma{(filters?.class_ids?.length || 0) > 0 ? ` · ${filters.class_ids.length}` : ''}</button>
                <select
                  className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white text-zinc-600 text-[10px]"
                  value={filters?.channel || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value || undefined }))}
                  data-testid="dnachat-filter-channel"
                >
                  <option value="">Canal · todos</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="dnachat">DNAChat</option>
                </select>
                <select
                  className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white text-zinc-600 text-[10px]"
                  value={filters?.status || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
                  data-testid="dnachat-filter-status"
                >
                  <option value="">Status · todos</option>
                  <option value="unread">Não lidas</option>
                  <option value="waiting">Aguardando</option>
                  <option value="replied">Respondidas</option>
                  <option value="failed">Falhas WA</option>
                  <option value="archived">Arquivadas</option>
                </select>
                <select
                  className="px-1.5 py-0.5 rounded border border-zinc-200 bg-white text-zinc-600 text-[10px]"
                  value={filters?.guardian_type || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, guardian_type: e.target.value || undefined }))}
                  data-testid="dnachat-filter-guardian-type"
                >
                  <option value="">Resp · todos</option>
                  <option value="financial">Financeiro</option>
                  <option value="pedagogical">Didático</option>
                </select>
                {advOpen && (
                  <div className="absolute left-0 top-6 z-30 w-64 bg-white border border-zinc-200 rounded-lg shadow-lg p-2 text-xs" data-testid="dnachat-class-picker">
                    <div className="text-[10px] uppercase text-zinc-500 mb-1 flex items-center justify-between">
                      <span>Filtrar por turmas</span>
                      {(filters?.class_ids?.length || 0) > 0 && (
                        <button type="button" className="text-[10px] text-indigo-600 hover:underline" onClick={() => setFilters((f) => ({ ...f, class_ids: [] }))}>Limpar</button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {(classList || []).map((c) => (
                        <label key={c.id} className="flex items-center gap-2 px-1 py-1 hover:bg-zinc-50 cursor-pointer">
                          <input type="checkbox" checked={(filters?.class_ids || []).includes(c.id)} onChange={() => toggleClassId(c.id)} data-testid={`dnachat-filter-class-opt-${c.id.slice(0, 8)}`} />
                          <span className="flex-1">{c.name}</span>
                          {typeof c.student_count === 'number' && <span className="text-[10px] text-zinc-400">{c.student_count}</span>}
                        </label>
                      ))}
                      {(classList || []).length === 0 && <div className="p-2 text-center text-[11px] text-zinc-400">Nenhuma turma</div>}
                    </div>
                  </div>
                )}
              </div>
              {/* V4.81 · Linha 3 · Botão SELECIONAR sempre visível (sidebar estreita) */}
              <div className="flex items-center gap-1 mt-1.5">
                <button
                  type="button"
                  onClick={() => { setSelectionMode((v) => { if (v) setSelectedIds(new Set()); return !v; }); }}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold ${selectionMode ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'}`}
                  data-testid="dnachat-selection-toggle"
                  title={selectionMode ? 'Sair do modo seleção' : 'Selecionar várias conversas para arquivar/restaurar'}
                >
                  <CheckSquare className="w-3.5 h-3.5" />
                  {selectionMode ? 'Sair' : 'Selecionar'}
                </button>
                {!selectionMode && (
                  <span className="text-[10px] text-zinc-400 hidden sm:inline">para arquivar/restaurar em massa</span>
                )}
              </div>
              {/* V4.80 · Barra de ações da seleção */}
              {selectionMode && (
                <div className="flex items-center gap-1 mt-1.5 p-1.5 bg-indigo-50 border border-indigo-200 rounded text-[11px] sticky top-0 z-20" data-testid="dnachat-selection-bar">
                  <span className="font-semibold text-indigo-800" data-testid="dnachat-selection-count">{selectedIds.size} selecionada{selectedIds.size === 1 ? '' : 's'}</span>
                  <button type="button" className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100" onClick={selectAllVisible} data-testid="dnachat-selection-all">Todas visíveis</button>
                  <button type="button" className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100" onClick={clearSelection}>Desmarcar</button>
                  {isArchivedView ? (
                    <button type="button" disabled={selectedIds.size === 0} onClick={() => onBulkRestore?.(Array.from(selectedIds))} className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40" data-testid="dnachat-bulk-restore">Restaurar</button>
                  ) : (
                    <button type="button" disabled={selectedIds.size === 0} onClick={() => onBulkArchive?.(Array.from(selectedIds))} className="text-[10px] px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40" data-testid="dnachat-bulk-archive">Arquivar</button>
                  )}
                </div>
              )}
            </div>
            {filteredVisible
              .map((c) => {
                const cid = String(c?.id || '');
                const cidShort = cid.slice(0, 8) || 'unknown';
                const isDirect = c.type === 'direct';
                const students = c.students || [];
                const primaryLine = c.display_name || c.name || 'Sem nome';
                const secondaryStudents = students.length > 0
                  ? students.slice(0, 2).map((s) => `${s.name || 'Aluno'}${s.class_name ? ` · ${s.class_name}` : ''}`).join(' | ')
                  : null;
                const extraCount = students.length > 2 ? ` | +${students.length - 2} alunos` : '';
                const phoneDisplay = c.phone
                  ? (String(c.phone).replace(/^\+?55/, '').replace(/\D/g, '').replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3'))
                  : null;
                const isSel = selectionMode && selectedIds.has(cid);
                return (
                  <div
                    key={cid || `noid-${cidShort}`}
                    onClick={() => { if (selectionMode) { toggleSel(cid); } else { onSelect(c); } }}
                    className={`group px-3 py-2.5 flex items-center gap-3 cursor-pointer border-b border-zinc-100 hover:bg-zinc-50 relative ${selected?.id === c.id && !selectionMode ? 'bg-indigo-50' : ''} ${isSel ? 'bg-indigo-100/70' : ''}`}
                    data-testid={`dnachat-conv-${cidShort}`}
                  >
                    {selectionMode && (
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={(e) => { e.stopPropagation(); toggleSel(cid); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4"
                        data-testid={`dnachat-conv-check-${cidShort}`}
                      />
                    )}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {primaryLine.slice(0, 1).toUpperCase()}
                    </div>
                    {/* V4.68 · Menu de ações da conversa (Arquivar/Excluir) */}
                    <ConvActionMenu conv={c} onArchive={onArchive} onDelete={onDelete} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-zinc-800 truncate">{primaryLine}</div>
                        <div className="text-[10px] text-zinc-400 flex-shrink-0 ml-2">{fmtTime(c.last_message_at)}</div>
                      </div>
                      {isDirect && secondaryStudents && (
                        <div className="text-[11px] text-indigo-700 truncate" title={secondaryStudents}>
                          {students.length === 1 ? 'Responsável por ' : ''}{secondaryStudents}{extraCount}
                        </div>
                      )}
                      {isDirect && phoneDisplay && (
                        <div className="text-[10px] text-zinc-500 flex items-center gap-1.5">
                          <span className="font-mono">{phoneDisplay}</span>
                          {c.whatsapp_url && (
                            <a
                              href={c.whatsapp_url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Abrir no WhatsApp"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition"
                              data-testid={`dnachat-wa-${c.id.slice(0, 8)}`}
                            >
                              <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.6L2 22l4.5-1.5A11 11 0 1 0 20.5 3.5zM12 20a8 8 0 0 1-4.3-1.2l-.3-.2-2.7.9.9-2.6-.2-.3A8 8 0 1 1 12 20zm4.3-5.9c-.2-.1-1.4-.7-1.6-.8s-.4 0-.5.2l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 0 1-3.2-2.8c-.2-.3 0-.4.1-.6l.4-.5c.1-.1.2-.3 0-.5s-.7-1.7-.9-2.3-.5-.5-.7-.5h-.5c-.2 0-.5.1-.7.3a2 2 0 0 0-.6 1.5c0 .9.6 1.8.7 1.9.1.2 1.4 2.2 3.4 3s2 .5 2.4.5 1.3-.6 1.5-1.1c.2-.5.2-1 .1-1.1s-.2-.2-.4-.2z"/></svg>
                              WA
                            </a>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-zinc-500 truncate">
                          {c.last_message_preview || (c.type === 'channel' ? '📡 canal' : c.type === 'broadcast' ? '📢 transmissão' : '—')}
                        </div>
                        {c.unread > 0 && (
                          <div className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center">{c.unread}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            {conversations.length === 0 && (
              <div className="p-6 text-center text-xs text-zinc-400 space-y-2" data-testid="dnachat-empty-state">
                {(searchActive || activeFilterCount > 0) ? (
                  <>
                    <div>Nenhuma conversa encontrada com estes filtros.</div>
                    <div className="flex gap-1 justify-center">
                      <button type="button" className="px-2 py-1 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700" onClick={onClearFilters} data-testid="dnachat-empty-clear">Limpar filtros</button>
                      <button type="button" className="px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onNewChat} data-testid="dnachat-empty-new">Nova conversa</button>
                    </div>
                  </>
                ) : (
                  <>Nenhuma conversa. Clique <Plus className="w-3 h-3 inline" /> para iniciar.</>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-2 border-t border-zinc-200 flex items-center gap-1">
        <Button size="sm" variant="ghost" className="flex-1 text-[11px]" onClick={onNewGroup} data-testid="dnachat-new-group">
          <Users className="w-3.5 h-3.5 mr-1" />Novo grupo
        </Button>
        <Button size="sm" variant="ghost" className="flex-1 text-[11px]" onClick={onNewBroadcast} data-testid="dnachat-new-broadcast">
          <Megaphone className="w-3.5 h-3.5 mr-1" />Transmissão
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────  MENSAGEM  ────────────────────────
// V4.63 · Renderiza anexos unificados (DNAChat nativo + WhatsApp inbound persistido)
// ────────────────────────  V4.73 · GRAVADOR DE ÁUDIO NO COMPOSER  ────────────────────────
function AudioRecorder({ isWaConversation, onSend }) {
  const [state, setState] = React.useState('idle'); // idle | recording | preview
  const [seconds, setSeconds] = React.useState(0);
  const [blob, setBlob] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timerRef = React.useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimes = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'];
      const mime = mimes.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        setState('preview');
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      };
      recRef.current = rec;
      setSeconds(0);
      rec.start();
      setState('recording');
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error('Permita acesso ao microfone para gravar áudio.');
    }
  };

  const stop = () => { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); };

  const cancel = () => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (previewUrl) { URL.revokeObjectURL(previewUrl); }
    setBlob(null); setPreviewUrl(null); setSeconds(0); setState('idle');
  };

  const send = async () => {
    if (!blob) return;
    const b = blob;
    cancel();
    await onSend(b);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (state === 'idle') {
    return (
      <Button size="sm" variant="ghost" onClick={start} title={isWaConversation ? 'Gravar áudio para WhatsApp' : 'Gravar áudio'} data-testid="dnachat-mic-btn">
        🎤
      </Button>
    );
  }
  if (state === 'recording') {
    return (
      <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 border border-rose-200" data-testid="dnachat-mic-recording">
        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
        <span className="text-xs font-mono text-rose-700">{fmt(seconds)}</span>
        <button onClick={stop} className="text-xs px-2 py-0.5 rounded bg-rose-600 text-white hover:bg-rose-700" data-testid="dnachat-mic-stop">Parar</button>
        <button onClick={cancel} className="text-xs px-2 py-0.5 rounded bg-zinc-200 text-zinc-700 hover:bg-zinc-300">✕</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 px-1 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="dnachat-mic-preview">
      <audio src={previewUrl} controls className="h-8" style={{ maxWidth: 160 }} />
      <button onClick={send} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="dnachat-mic-send">Enviar</button>
      <button onClick={cancel} className="text-xs px-2 py-1 rounded bg-zinc-200 text-zinc-700 hover:bg-zinc-300">✕</button>
    </div>
  );
}



function MessageAttachment({ msg, api, mine }) {
  const media = msg.media || {};
  const attId = msg.attachment_id || media.attachment_id;
  const attStatus = media.status || (attId ? 'downloaded' : null);
  const mime = media.mime || media.mime_type || '';
  const name = media.name || media.filename || media.caption || 'arquivo';

  // Anexo persistido via WhatsApp inbound (fonte de verdade: storage próprio)
  if (attId) {
    const viewUrl = `${api}/api/dnachat/attachments/${attId}/view`;
    const dlUrl = `${api}/api/dnachat/attachments/${attId}/download`;
    if (attStatus !== 'downloaded') {
      return (
        <div className="mb-1 text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Anexo aguardando processamento…
        </div>
      );
    }
    if (mime.startsWith('image/')) {
      return (
        <div className="mb-1">
          <a href={viewUrl} target="_blank" rel="noreferrer">
            <img src={viewUrl} alt={name} className="rounded max-w-xs cursor-zoom-in" />
          </a>
          <div className={`flex gap-2 mt-1 text-[10px] ${mine ? 'text-indigo-100' : 'text-slate-500'}`}>
            <a href={dlUrl} className="inline-flex items-center gap-0.5 underline" data-testid={`msg-att-download-${attId.slice(0, 8)}`}>
              <Paperclip className="w-3 h-3" /> Baixar
            </a>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 underline"
              onClick={() => { const w = window.open(viewUrl, '_blank'); if (w) setTimeout(() => { try { w.print(); } catch (_) { /* noop */ } }, 700); }}
              data-testid={`msg-att-print-${attId.slice(0, 8)}`}
            >🖨 Imprimir</button>
          </div>
        </div>
      );
    }
    if (mime === 'application/pdf') {
      return (
        <div className="mb-1">
          <a href={viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline">
            <Paperclip className="w-3 h-3" /> {name} (PDF)
          </a>
          <div className={`flex gap-2 mt-1 text-[10px] ${mine ? 'text-indigo-100' : 'text-slate-500'}`}>
            <a href={dlUrl} className="underline">Baixar</a>
            <button type="button" className="underline"
              onClick={() => { const w = window.open(viewUrl, '_blank'); if (w) setTimeout(() => { try { w.print(); } catch (_) { /* noop */ } }, 900); }}>
              🖨 Imprimir
            </button>
          </div>
        </div>
      );
    }
    if (mime.startsWith('audio/')) {
      // V4.69 · Player com duração pré-calculada (evita 0:00 quando browser não lê OGG/Opus metadata)
      const dur = media.duration_seconds || media.duration || null;
      const durLabel = dur ? `${Math.floor(dur / 60)}:${String(Math.floor(dur % 60)).padStart(2, '0')}` : null;
      return (
        <div className="mb-1">
          <audio controls preload="metadata" src={viewUrl} className="max-w-xs" />
          <div className={`mt-0.5 text-[10px] flex items-center gap-2 ${mine ? 'text-indigo-100' : 'text-slate-500'}`}>
            {durLabel && <span className="font-mono">🎵 {durLabel}</span>}
            <a href={dlUrl} className="underline">Baixar</a>
          </div>
        </div>
      );
    }
    if (mime.startsWith('video/')) {
      return (
        <div className="mb-1">
          <video controls src={viewUrl} className="max-w-xs rounded" />
          <div className="mt-0.5"><a href={dlUrl} className={`text-[10px] underline ${mine ? 'text-indigo-100' : 'text-slate-500'}`}>Baixar</a></div>
        </div>
      );
    }
    return (
      <div className="mb-1">
        <a href={dlUrl} className="inline-flex items-center gap-1 text-xs underline">
          <Paperclip className="w-3 h-3" /> {name} <em className="opacity-70">({mime || 'arquivo'})</em>
        </a>
      </div>
    );
  }

  // Legado: mensagem enviada pelo próprio DNAChat com media.url
  if (media.url) {
    if ((media.mime || '').startsWith('image/')) {
      return <div className="mb-1"><img src={`${api}${media.url}`} alt="" className="rounded max-w-xs" /></div>;
    }
    return (
      <div className="mb-1">
        <a href={`${api}${media.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline">
          <Paperclip className="w-3 h-3" /> {name}
        </a>
      </div>
    );
  }
  // Mídia inbound com provider_media_id mas ainda sem download persistido
  if (media.provider_media_id) {
    return (
      <div className="mb-1 text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Anexo do WhatsApp aguardando processamento…
      </div>
    );
  }
  return null;
}

// ────────────────────────  V4.89 · RELATÓRIO DE TRANSMISSÃO  ────────────────────────
function BroadcastReportDialog({ broadcastId, open, onClose }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [totals, setTotals] = React.useState({});
  const [recipients, setRecipients] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('all');

  React.useEffect(() => {
    if (!open || !broadcastId) return;
    let alive = true;
    setLoading(true);
    setError('');
    api.get(`/dnachat/whatsapp/broadcast/${broadcastId}/report`)
      .then(({ data }) => {
        if (!alive) return;
        setTotals(data?.totals || {});
        setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Falha ao carregar relatório'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, broadcastId]);

  const statusLabel = (s) => ({
    queued: 'Na fila', pending: 'Pendente', sending: 'Enviando',
    sent: 'Enviada', delivered: 'Entregue', read: 'Lida', failed: 'Falhou',
  }[s] || s || '—');

  const statusColor = (s) => ({
    queued: 'bg-slate-100 text-slate-700 border-slate-200',
    pending: 'bg-slate-100 text-slate-700 border-slate-200',
    sending: 'bg-blue-100 text-blue-700 border-blue-200',
    sent: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    delivered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    read: 'bg-emerald-200 text-emerald-900 border-emerald-300',
    failed: 'bg-rose-100 text-rose-700 border-rose-200',
  }[s] || 'bg-slate-100 text-slate-700 border-slate-200');

  const filtered = statusFilter === 'all'
    ? recipients
    : recipients.filter((r) => (r.status || 'queued') === statusFilter);

  const chips = [
    ['all', `Todos · ${totals.total || recipients.length || 0}`],
    ['sent', `Enviadas · ${totals.sent || 0}`],
    ['delivered', `Entregues · ${totals.delivered || 0}`],
    ['read', `Lidas · ${totals.read || 0}`],
    ['failed', `Falhas · ${totals.failed || 0}`],
    ['queued', `Fila · ${(totals.queued || 0) + (totals.pending || 0)}`],
  ];

  const exportCSV = () => {
    const header = ['Responsavel', 'Telefone', 'Aluno', 'Turma', 'Status', 'Erro'];
    const rows = filtered.map((r) => {
      const st = (r.students || []).map((s) => s.name).filter(Boolean).join(' | ');
      const cl = (r.students || []).map((s) => s.class_name).filter(Boolean).join(' | ');
      const err = r.meta_error?.title || r.error_detail || '';
      return [r.guardian_name || '', r.phone || r.normalized_phone || '',
        st, cl, statusLabel(r.status), String(err).replace(/[\r\n,]/g, ' ')];
    });
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `broadcast-${String(broadcastId).slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="dnachat-broadcast-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-indigo-600" />
            Relatório da transmissão
            <span className="text-xs font-normal text-zinc-500">· {String(broadcastId || '').slice(0, 12)}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1.5 pb-2 border-b">
          {chips.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setStatusFilter(k)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition ${statusFilter === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}
              data-testid={`dnachat-broadcast-report-filter-${k}`}
            >{label}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={exportCSV}
              disabled={loading || filtered.length === 0}
              className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50"
              data-testid="dnachat-broadcast-report-csv"
            >📄 CSV</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-zinc-500 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando relatório…
            </div>
          ) : error ? (
            <div className="py-10 text-center text-rose-600 text-sm" data-testid="dnachat-broadcast-report-error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-zinc-500 text-sm">Nenhum destinatário para este filtro.</div>
          ) : (
            <table className="w-full text-xs" data-testid="dnachat-broadcast-report-table">
              <thead className="sticky top-0 bg-zinc-50 z-10">
                <tr className="text-left text-zinc-600 border-b">
                  <th className="py-2 px-2">Responsável</th>
                  <th className="py-2 px-2">Aluno · Turma</th>
                  <th className="py-2 px-2">WhatsApp</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const students = r.students || [];
                  const err = r.meta_error?.title || r.error_detail;
                  return (
                    <tr key={r.queue_id || idx} className="border-b hover:bg-zinc-50" data-testid={`dnachat-broadcast-report-row-${idx}`}>
                      <td className="py-2 px-2 font-medium text-zinc-800">{r.guardian_name || '—'}</td>
                      <td className="py-2 px-2 text-zinc-700">
                        {students.length === 0 ? '—' : students.map((s, i) => (
                          <div key={i} className="text-[11px]">
                            <span className="font-medium">{s.name || '—'}</span>
                            {s.class_name && <span className="text-zinc-500"> · {s.class_name}</span>}
                          </div>
                        ))}
                      </td>
                      <td className="py-2 px-2 font-mono text-[11px] text-zinc-700">{r.phone || r.normalized_phone || '—'}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusColor(r.status)}`}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-[11px] text-rose-600 max-w-[220px] truncate" title={err || ''}>{err || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="pt-3 border-t">
          <div className="text-[11px] text-zinc-500 mr-auto">
            Mostrando <b>{filtered.length}</b> de <b>{recipients.length}</b>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="dnachat-broadcast-report-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MessageBubble({ msg, me, onReact, onDelete, onEdit, onPin, onFav, onReply, onAction, onRetry, onOpenBroadcastReport }) {
  const mine = msg.sender_id === me?.id;
  const [hover, setHover] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  if (msg.deleted_at) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'} px-3`}>
        <div className="italic text-xs text-zinc-400 my-1">🗑 Mensagem apagada</div>
      </div>
    );
  }
  const isSystem = msg.system || msg.sender_id === 'system-ai' || msg.sender_id === 'system-notifications';
  return (
    <div
      className={`flex ${mine ? 'justify-end' : 'justify-start'} px-3 my-1 group relative`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setShowEmoji(false); }}
      data-testid={`dnachat-msg-${msg.id.slice(0, 8)}`}
    >
      <div className={`max-w-[70%] rounded-2xl px-3 py-2 shadow-sm ${mine ? 'bg-indigo-600 text-white' : isSystem ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 text-zinc-800' : 'bg-white border border-zinc-200 text-zinc-800'}`}>
        {!mine && !isSystem && (
          <div className="text-[10px] font-semibold text-purple-700 mb-0.5">{msg.sender_name}</div>
        )}
        {isSystem && (
          <div className="text-[10px] font-semibold text-purple-700 mb-0.5 flex items-center gap-1">
            <Bot className="w-3 h-3" />{msg.sender_name}
          </div>
        )}
        {msg.media && (
          <MessageAttachment msg={msg} api={API} mine={mine} />
        )}
        {(msg.broadcast_id && msg.template) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenBroadcastReport?.(msg.broadcast_id); }}
            className={`text-[10px] font-semibold mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full transition hover:scale-105 ${mine ? 'bg-indigo-500/40 text-white hover:bg-indigo-500/60' : 'bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200'}`}
            title="Ver relatório da transmissão"
            data-testid={`dnachat-broadcast-tag-${msg.id.slice(0, 8)}`}
          >
            <Megaphone className="w-3 h-3" /> Transmissão · Ver relatório
          </button>
        )}
        {msg.body && <div className="text-sm whitespace-pre-wrap break-words">{msg.body}</div>}
        {Array.isArray(msg.actions) && msg.actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`dnachat-actions-${msg.id.slice(0, 8)}`}>
            {msg.actions.map((a, idx) => {
              const done = (msg.actions_executed || []).some((x) => x.action === a.action);
              const styleCls = a.style === 'primary'
                ? 'bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50'
                : a.style === 'secondary'
                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                  : 'bg-transparent text-purple-700 hover:bg-purple-50 border border-transparent';
              return (
                <button
                  key={idx}
                  className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition ${styleCls} ${done ? 'opacity-60' : ''}`}
                  onClick={() => onAction?.(msg, a)}
                  disabled={done}
                  data-testid={`dnachat-action-${a.action}-${idx}`}
                >
                  {done ? '✓ ' : ''}{a.label}
                </button>
              );
            })}
          </div>
        )}
        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
          <div className="flex gap-1 mt-1 text-xs">
            {Object.entries(msg.reactions).map(([e, users]) => (
              <span key={e} className={`px-1.5 rounded-full ${mine ? 'bg-indigo-500' : 'bg-zinc-100'}`}>{e} {users.length}</span>
            ))}
          </div>
        )}
        <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? 'text-indigo-200 justify-end' : 'text-zinc-400'}`}>
          {msg.edited_at && <span className="italic">editada</span>}
          {msg.pinned && <Pin className="w-3 h-3" />}
          {fmtTime(msg.created_at)}
          {/* V4.66 · Semântica de status Meta:
               sending  → spinner
               sent     → ✓  (aceito pela Meta, sem webhook delivered ainda)
               delivered→ ✓✓
               read     → ✓✓ (azul)
               failed   → alerta */}
          {mine && (() => {
            const isWaOut = msg.channel === 'whatsapp' && msg.direction === 'outbound';
            // WA outbound: server status (webhook) tem precedência sobre estado otimista
            const s = isWaOut ? (msg.status || msg._status) : (msg._status || msg.status);
            if (s === 'sending' || s === 'queued') return <Loader2 className="w-3 h-3 animate-spin" />;
            if (s === 'failed') return (
              <button
                className="text-rose-200 hover:text-white underline text-[10px]"
                onClick={(e) => { e.stopPropagation(); onRetry?.(msg); }}
                title={msg.error_detail || msg.meta_error?.cause || 'Falha ao enviar'}
                data-testid={`dnachat-retry-${msg.id.slice(0, 8)}`}
              >Falha — tentar novamente</button>
            );
            if (s === 'read')       return <CheckCheck className="w-3 h-3 text-sky-300" />;
            if (s === 'delivered')  return <CheckCheck className="w-3 h-3" />;
            if (s === 'sent') {
              // WA outbound: só ✓ até chegar webhook delivered
              return isWaOut
                ? <Check className="w-3 h-3" />
                : <CheckCheck className="w-3 h-3" />;
            }
            // Sem status conhecido → não fabricar ✓✓ para WA outbound
            if (isWaOut) return <Loader2 className="w-3 h-3 animate-spin opacity-60" />;
            return <CheckCheck className="w-3 h-3" />;
          })()}
        </div>
      </div>
      {hover && !isSystem && (
        <div className={`absolute ${mine ? 'right-2' : 'left-2'} -top-3 bg-white border border-zinc-200 rounded-full shadow px-1 py-0.5 flex items-center gap-1 z-10`}>
          <button className="p-1 hover:bg-zinc-100 rounded-full" onClick={() => setShowEmoji((s) => !s)} title="Reação"><Smile className="w-3 h-3" /></button>
          <button className="p-1 hover:bg-zinc-100 rounded-full" onClick={() => onReply(msg)} title="Responder"><Reply className="w-3 h-3" /></button>
          <button className="p-1 hover:bg-zinc-100 rounded-full" onClick={() => onFav(msg)} title="Favoritar"><Star className="w-3 h-3" /></button>
          <button className="p-1 hover:bg-zinc-100 rounded-full" onClick={() => onPin(msg)} title="Fixar"><Pin className="w-3 h-3" /></button>
          {mine && <button className="p-1 hover:bg-zinc-100 rounded-full" onClick={() => onEdit(msg)} title="Editar"><Edit3 className="w-3 h-3" /></button>}
          {mine && <button className="p-1 hover:bg-red-100 rounded-full text-red-600" onClick={() => onDelete(msg)} title="Excluir"><Trash2 className="w-3 h-3" /></button>}
          {showEmoji && (
            <div className={`absolute ${mine ? 'right-0' : 'left-0'} top-8 bg-white border border-zinc-200 rounded-full shadow px-2 py-1 flex gap-1`}>
              {emojis.map((e) => (
                <button key={e} className="hover:scale-125 transition" onClick={() => { onReact(msg, e); setShowEmoji(false); }}>{e}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────  ÁREA DE CHAT  ────────────────────────
function ChatArea({ me, conv, onReload }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  // V4.64 · Janela 24h + seletor de template inline
  const [waWindow, setWaWindow] = useState(null); // { active, seconds_left, until }
  const [waTemplates, setWaTemplates] = useState([]);
  const [tplPicker, setTplPicker] = useState(false);
  // V4.89 · Relatório da transmissão (broadcast report)
  const [reportBroadcastId, setReportBroadcastId] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const messagesRef = useRef([]);   // espelho para uso dentro de intervals sem re-criar timer
  const sendingRef = useRef(false); // trava anti-duplo-clique/enter
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // V4.66 · Roteamento por canal — inclui `responsible_id` + `normalized_phone`
  // (o backend `_enrich_conversation` popula esses campos para conversas 1:1 com
  // responsáveis mesmo quando `guardian_ref` não estiver setado no doc).
  const guardianId = conv?.guardian_ref || conv?.responsible_id || null;
  const guardianPhone = conv?.guardian_phone || conv?.normalized_phone || null;
  const isWaConversation = !!(guardianId && guardianPhone);

  // V4.66 · Ao trocar de conversa, se resolve responsável (guardian_ref OU responsible_id) → checa janela + carrega templates
  useEffect(() => {
    if (!guardianId) { setWaWindow(null); return; }
    let alive = true;
    api.get(`/dnachat/whatsapp/service-window/${guardianId}`)
      .then(({ data }) => { if (alive) setWaWindow(data || null); })
      .catch(() => { if (alive) setWaWindow(null); });
    if (waTemplates.length === 0) {
      api.get('/dnachat/whatsapp/templates')
        .then(({ data }) => { if (alive) setWaTemplates((data?.items || []).filter((t) => (t.status || '').toUpperCase() === 'APPROVED')); })
        .catch(() => {});
    }
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [guardianId]);

  // Merge deduplicado por id, client_message_id, message_uuid e provider_message_id (V4.66)
  const mergeMessages = (prev, incoming) => {
    if (!incoming || !incoming.length) return prev;
    const byId = new Map();
    const byCmid = new Map();
    const byUuid = new Map();
    const byPid = new Map();
    for (const m of prev) {
      if (m.id) byId.set(m.id, m);
      if (m.client_message_id) byCmid.set(m.client_message_id, m);
      if (m.message_uuid) byUuid.set(m.message_uuid, m);
      if (m.provider_message_id) byPid.set(m.provider_message_id, m);
    }
    for (const inc of incoming) {
      // 1) já existe pelo id real → substitui (mantém status do server, sem forçar 'sent')
      if (inc.id && byId.has(inc.id)) {
        byId.set(inc.id, { ...byId.get(inc.id), ...inc });
        continue;
      }
      // 2) reconcilia otimista pelo client_message_id
      if (inc.client_message_id && byCmid.has(inc.client_message_id)) {
        const opt = byCmid.get(inc.client_message_id);
        byId.delete(opt.id);
        const merged = { ...opt, ...inc };
        byId.set(inc.id || opt.id, merged);
        byCmid.set(inc.client_message_id, merged);
        continue;
      }
      // 3) V4.66 · reconcilia por message_uuid (backend WA usa message_uuid como idempotência)
      if (inc.message_uuid && byUuid.has(inc.message_uuid)) {
        const opt = byUuid.get(inc.message_uuid);
        byId.delete(opt.id);
        const merged = { ...opt, ...inc };
        byId.set(inc.id || opt.id, merged);
        byUuid.set(inc.message_uuid, merged);
        continue;
      }
      // 4) V4.66 · reconcilia por provider_message_id (Meta ID)
      if (inc.provider_message_id && byPid.has(inc.provider_message_id)) {
        const opt = byPid.get(inc.provider_message_id);
        byId.delete(opt.id);
        const merged = { ...opt, ...inc };
        byId.set(inc.id || opt.id, merged);
        byPid.set(inc.provider_message_id, merged);
        continue;
      }
      // 5) mensagem nova (não força _status='sent' — deixa server dizer)
      byId.set(inc.id, { ...inc });
      if (inc.client_message_id) byCmid.set(inc.client_message_id, byId.get(inc.id));
      if (inc.message_uuid) byUuid.set(inc.message_uuid, byId.get(inc.id));
      if (inc.provider_message_id) byPid.set(inc.provider_message_id, byId.get(inc.id));
    }
    return Array.from(byId.values()).sort((a, b) => {
      const da = a.created_at || '';
      const db2 = b.created_at || '';
      if (da === db2) return String(a.id || '').localeCompare(String(b.id || ''));
      return da < db2 ? -1 : 1;
    });
  };

  const load = async (append = false) => {
    if (!conv) return;
    try {
      const params = {};
      const cur = messagesRef.current;
      // usa a última mensagem "sent" (não otimista) para paginar
      const lastSent = [...cur].reverse().find((m) => m._status !== 'sending' && m.created_at);
      if (append && lastSent) params.since_ts = lastSent.created_at;
      const { data } = await api.get(`/dnachat/conversations/${conv.id}/messages`, { params });
      const incoming = data.messages || [];
      if (append) {
        setMessages((prev) => mergeMessages(prev, incoming));
      } else {
        setMessages((prev) => mergeMessages(
          // preserva otimistas em sending que ainda não voltaram
          prev.filter((m) => m._status === 'sending'),
          incoming
        ));
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { setMessages([]); load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [conv?.id]);
  // polling estável: NÃO depende de `messages` (usa messagesRef)
  useEffect(() => {
    if (!conv) return;
    const t = setInterval(() => load(true), 3500);
    return () => clearInterval(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [conv?.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);
  useEffect(() => { if (conv) api.post(`/dnachat/conversations/${conv.id}/read`).catch(() => {}); }, [conv?.id, messages.length]);

  const genCmid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const doSend = async (payload, optimistic) => {
    // Insere otimista imediatamente (uma única vez)
    setMessages((prev) => mergeMessages(prev, [optimistic]));
    try {
      const { data } = await api.post(`/dnachat/conversations/${conv.id}/messages`, payload);
      // Reconcilia — pode vir sem client_message_id no caso de conversa direta antes do fix; mesmo assim mergeMessages resolve
      setMessages((prev) => mergeMessages(prev, [{ ...(data || {}), client_message_id: payload.client_message_id, _status: 'sent' }]));
      onReload?.();
      return true;
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.client_message_id === payload.client_message_id ? { ...m, _status: 'failed' } : m)));
      toast.error(e?.response?.data?.detail || 'Falha ao enviar');
      return false;
    }
  };

  // V4.65 · Envia pelo canal WhatsApp real (chama Meta via backend) e reconcilia otimista
  const doSendWhatsApp = async (payload, optimistic) => {
    setMessages((prev) => mergeMessages(prev, [optimistic]));
    try {
      // Diagnóstico (removível): confirma que o botão do composer chegou ao provider WhatsApp
      // eslint-disable-next-line no-console
      console.info('[DNACHAT_SEND]', {
        conversation_id: conv?.id,
        guardian_id: guardianId,
        client_message_id: payload.client_message_id,
        provider: 'whatsapp',
        endpoint: '/dnachat/whatsapp/send',
      });
      const { data } = await api.post('/dnachat/whatsapp/send', {
        guardian_id: guardianId,
        text: payload.body || undefined,
        template_name: payload.template_name || undefined,
        template_language: payload.template_language || undefined,
        template_variables: payload.template_variables || undefined,
        media_id: payload.media_id || undefined,
        media_type: payload.media_type || undefined,
        media_filename: payload.media_filename || undefined,
        caption: payload.caption || undefined,
        message_uuid: payload.client_message_id,
      });
      if (data?.success) {
        setMessages((prev) => prev.map((m) => (
          m.client_message_id === payload.client_message_id
            ? { ...m, id: m.id, channel: 'whatsapp', direction: 'outbound',
                message_uuid: data.message_uuid || payload.client_message_id,
                provider_message_id: data.provider_message_id, status: 'sent', _status: 'sent' }
            : m
        )));
        onReload?.();
        // Rebusca janela 24h (envio de template não abre; texto livre só é possível se já aberta)
        if (guardianId) {
          api.get(`/dnachat/whatsapp/service-window/${guardianId}`).then(({ data: w }) => setWaWindow(w)).catch(() => {});
        }
        return true;
      }
      // Meta rejeitou (janela fechada, template inválido, número, etc.)
      const meta = data?.meta_error || {};
      const category = meta.category || 'unknown';
      const cause = meta.cause || data?.error || 'Meta recusou o envio';
      setMessages((prev) => prev.map((m) => (
        m.client_message_id === payload.client_message_id
          ? { ...m, _status: 'failed', error_detail: cause, meta_error: meta }
          : m
      )));
      // Se for janela fechada, sugere template
      if (category === 'window_closed' || /24h|template|re-?open/i.test(cause)) {
        toast.error('Fora da janela de 24h — envie um template aprovado.', {
          duration: 8000, action: { label: 'Selecionar template', onClick: () => setTplPicker(true) },
        });
      } else {
        toast.error(`WhatsApp rejeitou: ${cause}`.slice(0, 240));
      }
      return false;
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data;
      const cause = typeof d === 'string' ? d : (d?.error || d?.meta_error?.cause || 'Falha ao enviar via WhatsApp');
      setMessages((prev) => prev.map((m) => (
        m.client_message_id === payload.client_message_id ? { ...m, _status: 'failed', error_detail: String(cause).slice(0, 300) } : m
      )));
      toast.error(String(cause).slice(0, 240));
      return false;
    }
  };

  const send = async () => {
    if (!conv) return;
    if (sendingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const cmid = genCmid();
    const now = new Date().toISOString();
    const optimistic = {
      id: `opt-${cmid}`,
      client_message_id: cmid,
      conversation_id: conv.id,
      sender_id: me?.id,
      sender_name: me?.name,
      body: trimmed,
      reply_to: replyTo?.id,
      created_at: now,
      reactions: {},
      _status: 'sending',
      channel: isWaConversation ? 'whatsapp' : 'dnachat',
    };
    sendingRef.current = true;
    setLoading(true);
    setText(''); setReplyTo(null);
    // V4.65 · Roteamento por canal: família → WhatsApp obrigatório
    let ok = false;
    if (isWaConversation) {
      // Janela 24h fechada → abre picker sem chamar Meta (evita gasto e erro certo)
      if (waWindow && waWindow.active === false) {
        setMessages((prev) => prev.map((m) => (
          m.client_message_id === cmid ? { ...m, _status: 'failed', error_detail: 'Fora da janela 24h — use template.' } : m
        )));
        toast.error('Fora da janela de 24h. Selecione um template aprovado.', {
          duration: 8000, action: { label: 'Selecionar template', onClick: () => setTplPicker(true) },
        });
        setText(trimmed);
      } else {
        ok = await doSendWhatsApp({ body: trimmed, client_message_id: cmid }, optimistic);
      }
    } else {
      ok = await doSend({ body: trimmed, reply_to: replyTo?.id, client_message_id: cmid }, optimistic);
    }
    sendingRef.current = false;
    setLoading(false);
    if (!ok && !isWaConversation) {
      // preserva texto para tentativa manual
      setText(trimmed);
    }
  };

  const retrySend = async (msg) => {
    if (!msg.client_message_id) return;
    setMessages((prev) => prev.map((m) => (m.client_message_id === msg.client_message_id ? { ...m, _status: 'sending' } : m)));
    if (isWaConversation) {
      await doSendWhatsApp({ body: msg.body, client_message_id: msg.client_message_id },
        { ...msg, _status: 'sending' });
    } else {
      await doSend({ body: msg.body, reply_to: msg.reply_to, client_message_id: msg.client_message_id },
        { ...msg, _status: 'sending' });
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // V4.65 · Se conversa de família → sobe pela pipeline Meta (upload → send)
    if (isWaConversation) {
      const cmid = genCmid();
      const now = new Date().toISOString();
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const upResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dnachat/whatsapp/upload`, {
          method: 'POST', body: fd,
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const up = await upResp.json();
        if (!upResp.ok) throw new Error(up?.detail?.provider_error?.error?.message || up?.detail || 'Falha no upload');
        const optimistic = {
          id: `opt-${cmid}`, client_message_id: cmid, conversation_id: conv.id,
          sender_id: me?.id, sender_name: me?.name,
          media: { name: up.filename, mime: up.mime, media_type: up.media_type, provider_media_id: up.media_id, status: 'downloaded' },
          body: text.trim() || undefined,
          created_at: now, reactions: {}, _status: 'sending', channel: 'whatsapp',
        };
        await doSendWhatsApp({
          media_id: up.media_id, media_type: up.media_type, media_filename: up.filename,
          caption: text.trim() || undefined, client_message_id: cmid,
        }, optimistic);
        setText('');
      } catch (err) {
        toast.error(String(err?.message || err).slice(0, 240));
      } finally {
        setLoading(false); e.target.value = '';
      }
      return;
    }
    // Legado: conversa interna DNAChat
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/dnachat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const cmid = genCmid();
      const now = new Date().toISOString();
      const optimistic = {
        id: `opt-${cmid}`, client_message_id: cmid, conversation_id: conv.id,
        sender_id: me?.id, sender_name: me?.name, media: data, created_at: now, reactions: {}, _status: 'sending',
      };
      await doSend({ media: data, client_message_id: cmid }, optimistic);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha no upload');
    } finally { e.target.value = ''; }
  };

  const onReact = async (msg, emoji) => {
    await api.post(`/dnachat/messages/${msg.id}/react`, { emoji });
    await load(false);
  };
  const onDelete = async (msg) => {
    if (!window.confirm('Excluir mensagem?')) return;
    await api.delete(`/dnachat/messages/${msg.id}`);
    await load(false);
  };
  const onEdit = async (msg) => {
    const novo = window.prompt('Editar mensagem', msg.body || '');
    if (novo == null) return;
    await api.post(`/dnachat/messages/${msg.id}/edit`, { body: novo });
    await load(false);
  };
  const onPin = async (msg) => {
    await api.post(`/dnachat/messages/${msg.id}/pin`, { pinned: !msg.pinned });
    await load(false);
  };
  const onFav = async (msg) => {
    const fav = !(msg.favorites && msg.favorites[me?.id]);
    await api.post(`/dnachat/messages/${msg.id}/favorite`, { favorite: fav });
    toast.success(fav ? 'Adicionado aos favoritos' : 'Removido dos favoritos');
  };

  const onAction = async (msg, action) => {
    try {
      const { data } = await api.post(`/dnachat/messages/${msg.id}/action`, {
        action: action.action,
        params: action.params || {},
      });
      // Feedback contextual
      if (action.action === 'open_url' && (action.params?.url || data.url)) {
        window.open(action.params?.url || data.url, '_blank');
      } else if (action.action === 'pay_pix') {
        toast.success('Abrindo pagamento…');
        window.open(data.deep_link || `/v2/dnaconnect/financeiro`, '_blank');
      } else if (action.action === 'send_boleto_wa') {
        toast.success('Solicitação enviada — boleto será entregue no WhatsApp em instantes.');
      } else if (action.action === 'contact_role' && data.conversation_id) {
        toast.success('Abrindo conversa…');
        // recarregar lista de conversas e selecionar a nova
        onReload?.();
      } else {
        toast.success('Ação executada');
      }
      await load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha na ação');
    }
  };

  if (!conv) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 bg-gradient-to-br from-zinc-50 to-indigo-50/20">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold mb-3">DNA</div>
        <div className="text-sm">Selecione uma conversa</div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col bg-gradient-to-br from-zinc-50 to-indigo-50/20" data-testid="dnachat-chat-area">
      <div className="px-4 py-3 border-b border-zinc-200 bg-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
          {(conv.display_name || conv.name || '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-800 truncate">{conv.display_name || conv.name}</div>
          <div className="text-[11px] text-zinc-500 capitalize">{conv.type}{guardianPhone ? ` · ${guardianPhone}` : ''}</div>
        </div>
        {/* V4.63 · Ações da conversa: imprimir + exportar PDF */}
        <div className="flex items-center gap-1">
          {conv?.type === 'broadcast' && (
            <button
              type="button"
              title="Relatório da transmissão"
              onClick={() => setReportBroadcastId(conv.id)}
              className="text-[11px] px-2 py-1 rounded-md bg-indigo-100 hover:bg-indigo-200 text-indigo-800 inline-flex items-center gap-1"
              data-testid="dnachat-open-broadcast-report"
            >📊 Relatório</button>
          )}
          <button
            type="button"
            title="Imprimir conversa"
            onClick={() => {
              const tk = localStorage.getItem('token') || '';
              window.open(`${API}/api/dnachat/conversations/${conv.id}/print?token=${encodeURIComponent(tk)}`, '_blank');
            }}
            className="text-[11px] px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"
            data-testid="dnachat-print-conv"
          >🖨 Imprimir</button>
          <button
            type="button"
            title="Exportar conversa em PDF"
            onClick={() => {
              const tk = localStorage.getItem('token') || '';
              window.open(`${API}/api/dnachat/conversations/${conv.id}/export.pdf?token=${encodeURIComponent(tk)}`, '_blank');
            }}
            className="text-[11px] px-2 py-1 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-800 inline-flex items-center gap-1"
            data-testid="dnachat-export-pdf"
          >📄 PDF</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3 space-y-1">
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            me={me}
            onReact={onReact}
            onDelete={onDelete}
            onEdit={onEdit}
            onPin={onPin}
            onFav={onFav}
            onReply={setReplyTo}
            onAction={onAction}
            onRetry={retrySend}
            onOpenBroadcastReport={setReportBroadcastId}
          />
        ))}
        <div ref={endRef} />
      </div>
      {/* V4.65 · Indicador de canal do composer principal */}
      {isWaConversation && (
        <div className="mx-4 mb-1 flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="dnachat-channel-indicator">
          <MessageCircle className="w-3.5 h-3.5" />
          <span><b>Enviando via WhatsApp</b> — para {guardianPhone || 'contato'}{waWindow?.active === false ? ' · fora da janela 24h' : ''}</span>
        </div>
      )}
      {replyTo && (
        <div className="mx-4 mb-1 px-3 py-1.5 bg-indigo-50 border-l-4 border-indigo-500 rounded-r text-xs flex items-center justify-between">
          <div><span className="font-semibold">Respondendo a {replyTo.sender_name}:</span> <span className="text-zinc-600">{(replyTo.body || '').slice(0, 80)}</span></div>
          <button onClick={() => setReplyTo(null)} className="text-zinc-400 hover:text-zinc-700">×</button>
        </div>
      )}
      {/* V4.64 · Aviso de janela 24h fechada + acesso a templates */}
      {isWaConversation && waWindow && !waWindow.active && (
        <div className="mx-4 mb-1 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs flex items-center justify-between gap-2" data-testid="dnachat-wa-window-closed">
          <div className="text-amber-800">
            <b>Fora da janela de atendimento do WhatsApp (24h).</b> Envie um template aprovado para reabrir a conversa.
          </div>
          <button type="button" onClick={() => setTplPicker(true)}
            className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="dnachat-open-tpl-picker">
            Selecionar template
          </button>
        </div>
      )}
      <div className="p-3 border-t border-zinc-200 bg-white flex items-end gap-2">
        <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="dnachat-file-input" />
        <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} title="Anexar" data-testid="dnachat-attach-btn">
          <Paperclip className="w-4 h-4" />
        </Button>
        {/* V4.73 · Gravador de áudio (MediaRecorder + upload direto Meta quando conv=WA) */}
        <AudioRecorder isWaConversation={isWaConversation} onSend={async (blob) => {
          const cmid = genCmid();
          const now = new Date().toISOString();
          if (isWaConversation) {
            setLoading(true);
            try {
              const fd = new FormData();
              fd.append('file', blob, `audio-${Date.now()}.webm`);
              const upResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dnachat/whatsapp/upload`, {
                method: 'POST', body: fd,
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
              });
              const up = await upResp.json();
              if (!upResp.ok) throw new Error(up?.detail?.provider_error?.error?.message || up?.detail || 'Falha no upload');
              const optimistic = {
                id: `opt-${cmid}`, client_message_id: cmid, conversation_id: conv.id,
                sender_id: me?.id, sender_name: me?.name,
                media: { name: up.filename, mime: up.mime, media_type: 'audio', provider_media_id: up.media_id, status: 'downloaded' },
                created_at: now, reactions: {}, _status: 'sending', channel: 'whatsapp',
              };
              await doSendWhatsApp({ media_id: up.media_id, media_type: 'audio', client_message_id: cmid }, optimistic);
            } catch (err) {
              toast.error(String(err?.message || err).slice(0, 240));
            } finally { setLoading(false); }
          } else {
            // Áudio em conversa interna DNAChat
            const fd = new FormData(); fd.append('file', blob, `audio-${Date.now()}.webm`);
            try {
              const { data } = await api.post('/dnachat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              const opt = { id: `opt-${cmid}`, client_message_id: cmid, conversation_id: conv.id, sender_id: me?.id, sender_name: me?.name, media: data, created_at: now, reactions: {}, _status: 'sending' };
              await doSend({ media: data, client_message_id: cmid }, opt);
            } catch (err) { toast.error(err?.response?.data?.detail || 'Falha no upload'); }
          }
        }} />
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Digite uma mensagem…"
          className="resize-none min-h-[40px] max-h-[120px]"
          rows={1}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid="dnachat-input"
        />
        <Button onClick={send} disabled={loading || !text.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="dnachat-send-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
        {isWaConversation && (
          <>
            <input
              ref={(el) => { window.__waFileRef = el; }}
              type="file"
              className="hidden"
              accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setLoading(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  const upResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dnachat/whatsapp/upload`, {
                    method: 'POST', body: fd,
                    headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                  });
                  const up = await upResp.json();
                  if (!upResp.ok) throw new Error(up?.detail?.provider_error?.error?.message || up?.detail || 'Falha no upload');
                  const { data } = await api.post('/dnachat/whatsapp/send', {
                    guardian_id: guardianId,
                    media_id: up.media_id,
                    media_type: up.media_type,
                    media_filename: up.filename,
                    caption: text.trim() || undefined,
                  });
                  if (data?.success) {
                    toast.success(`Mídia enviada pelo WhatsApp (${up.media_type})`);
                    setText('');
                    load(true);
                  } else { toast.error(data?.error || 'Falha ao enviar mídia'); }
                } catch (err) {
                  toast.error(String(err.message || err).slice(0, 240));
                } finally {
                  setLoading(false);
                  e.target.value = '';
                }
              }}
              data-testid="dnachat-wa-media-input"
            />
            <Button
              onClick={() => window.__waFileRef?.click()}
              disabled={loading}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              title="Enviar mídia pelo WhatsApp (PDF, imagem, vídeo, áudio)"
              data-testid="dnachat-send-wa-media-btn"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            <Button
              onClick={async () => {
                if (!text.trim()) return toast.error('Digite uma mensagem');
                setLoading(true);
                try {
                  const { data } = await api.post('/dnachat/whatsapp/send', {
                    guardian_id: guardianId, text: text.trim(),
                  });
                  if (data?.success) {
                    toast.success(`Enviado pelo WhatsApp ${data.provider_message_id ? '· ID ' + String(data.provider_message_id).slice(0, 8) : ''}`);
                    setText('');
                    load(true);
                  } else {
                    toast.error(data?.error || 'Falha ao enviar');
                  }
                } catch (e) {
                  const d = e?.response?.data?.detail;
                  toast.error(typeof d === 'string' ? d : JSON.stringify(d).slice(0, 200));
                } finally { setLoading(false); }
              }}
              disabled={loading || !text.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              title="Enviar pelo WhatsApp"
              data-testid="dnachat-send-whatsapp-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            </Button>
            {/* V4.64 · Templates inline (necessário quando janela 24h fechada) */}
            <Button
              type="button"
              onClick={() => setTplPicker(true)}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              title="Enviar template aprovado (Meta)"
              data-testid="dnachat-open-tpl-picker-btn"
            >
              🧩
            </Button>
          </>
        )}
      </div>
      {/* V4.64 · Modal de seleção de template */}
      <WATemplatePicker
        open={tplPicker}
        onClose={() => setTplPicker(false)}
        templates={waTemplates}
        onSend={async (tpl, variables) => {
          if (!guardianId) return;
          setLoading(true);
          try {
            const { data } = await api.post('/dnachat/whatsapp/send', {
              guardian_id: guardianId,
              template_name: tpl.name,
              template_language: tpl.language || 'pt_BR',
              template_variables: variables,
            });
            if (data?.success) {
              toast.success(`Template "${tpl.name}" enviado`);
              setTplPicker(false);
              load(true);
              // Após envio de template, a janela geralmente permanece fechada até a família responder
              api.get(`/dnachat/whatsapp/service-window/${guardianId}`).then(({ data: w }) => setWaWindow(w)).catch(() => {});
            } else {
              toast.error(data?.error || 'Falha ao enviar template');
            }
          } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : JSON.stringify(d).slice(0, 200));
          } finally { setLoading(false); }
        }}
      />
      <BroadcastReportDialog
        broadcastId={reportBroadcastId}
        open={!!reportBroadcastId}
        onClose={() => setReportBroadcastId(null)}
      />
    </div>
  );
}


// ────────────────────────  MODAL: SELETOR DE TEMPLATE WHATSAPP  ────────────────────────
// V4.64 · Seletor inline sem sair da conversa. Extrai variáveis {{1}} {{2}} do body.
function WATemplatePicker({ open, onClose, templates, onSend }) {
  const [selected, setSelected] = useState(null);
  const [vars, setVars] = useState([]);
  useEffect(() => { if (!open) { setSelected(null); setVars([]); } }, [open]);
  const bodyText = useMemo(() => {
    if (!selected) return '';
    const c = (selected.components || []).find((x) => (x.type || '').toUpperCase() === 'BODY');
    return (c?.text) || '';
  }, [selected]);
  const varCount = useMemo(() => {
    const m = bodyText.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    const nums = m.map((s) => parseInt(s.replace(/\D/g, ''), 10));
    return nums.length ? Math.max(...nums) : 0;
  }, [bodyText]);
  useEffect(() => { setVars(Array(varCount).fill('')); }, [varCount]);
  const preview = useMemo(() => {
    let out = bodyText;
    vars.forEach((v, i) => { out = out.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'), v || `{{${i + 1}}}`); });
    return out;
  }, [bodyText, vars]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dnachat-tpl-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><span>🧩</span> Enviar template WhatsApp</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded-lg">
            {(templates || []).length === 0 && (
              <div className="p-4 text-xs text-zinc-500">Nenhum template aprovado disponível. Sincronize os templates da Meta em <b>WhatsApp · Templates</b>.</div>
            )}
            {(templates || []).map((t) => (
              <button
                key={`${t.name}:${t.language}`}
                type="button"
                onClick={() => setSelected(t)}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-amber-50 ${selected?.name === t.name && selected?.language === t.language ? 'bg-amber-100' : ''}`}
                data-testid={`dnachat-tpl-opt-${t.name}`}
              >
                <div className="text-sm font-semibold text-zinc-800">{t.name}</div>
                <div className="text-[10px] text-zinc-500 uppercase">{t.language} · {t.category || '—'}</div>
              </button>
            ))}
          </div>
          <div className="min-h-[16rem] flex flex-col gap-2">
            {selected ? (
              <>
                <div className="text-[11px] uppercase text-zinc-500 font-semibold">Preview</div>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm whitespace-pre-wrap min-h-16">{preview || <em className="text-zinc-400">Sem conteúdo BODY.</em>}</div>
                {varCount > 0 && (
                  <div className="space-y-1 mt-1">
                    <div className="text-[11px] uppercase text-zinc-500 font-semibold">Variáveis</div>
                    {Array.from({ length: varCount }).map((_, i) => (
                      <Input key={i} value={vars[i] || ''}
                        onChange={(e) => setVars((cur) => { const n = [...cur]; n[i] = e.target.value; return n; })}
                        placeholder={`{{${i + 1}}}`} data-testid={`dnachat-tpl-var-${i + 1}`} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-zinc-500 p-3">Selecione um template para pré-visualizar.</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="dnachat-tpl-cancel">Cancelar</Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={!selected || vars.some((v, i) => i < varCount && !v.trim())}
            onClick={() => onSend(selected, vars.slice(0, varCount))}
            data-testid="dnachat-tpl-send">
            Enviar template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────────  BROADCAST DIALOG (TURMAS REAIS)  ────────────────────────
function BroadcastDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState(new Set());
  const [guardianType, setGuardianType] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [alsoWhatsApp, setAlsoWhatsApp] = useState(true);  // V4.76 · default ON — usuário escolhe template
  // V4.76 · Preview WhatsApp com janela 24h + templates aprovados
  const [waPreview, setWaPreview] = useState(null);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplLang, setTplLang] = useState('pt_BR');
  const [tplVarMap, setTplVarMap] = useState({});    // { "1": "guardian_name" | "text:..." | ... }
  const [sendMode, setSendMode] = useState('auto');  // auto | template_all
  const [syncingTpl, setSyncingTpl] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setName(''); setSelectedClassIds(new Set()); setGuardianType('all');
    setResolved(null); setExcluded(new Set()); setBody(''); setQ('');
    setWaPreview(null); setApprovedTemplates([]); setTplName(''); setTplVarMap({});
    setSendMode('auto');
    setLoading(true);
    api.get('/dnachat/broadcast/classes')
      .then((r) => setClasses(r.data?.classes || []))
      .finally(() => setLoading(false));
  }, [open]);

  const filteredClasses = classes.filter((c) => !q || (c.name || '').toLowerCase().includes(q.toLowerCase()));

  const toggleClass = (id) => setSelectedClassIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAllFiltered = () => setSelectedClassIds(new Set(filteredClasses.map((c) => c.id)));
  const clearAll = () => setSelectedClassIds(new Set());

  const resolve = async () => {
    if (!name.trim()) return toast.error('Informe o nome da lista.');
    if (selectedClassIds.size === 0) return toast.error('Selecione ao menos uma turma.');
    setLoading(true);
    try {
      const { data } = await api.post('/dnachat/broadcast/resolve', {
        class_ids: Array.from(selectedClassIds),
        guardian_type: guardianType,
      });
      if (!data || !Array.isArray(data.recipients) || data.recipients.length === 0) {
        toast.error('Nenhum responsável foi encontrado nas turmas selecionadas.');
        setResolved(data || null);
        setStep(2);
        return;
      }
      setResolved(data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar os destinatários.');
    } finally { setLoading(false); }
  };

  // V4.76 · Etapa 3 — Prévia WhatsApp (janela 24h + templates aprovados)
  const loadWaPreview = async () => {
    try {
      const gtMap = { financial: 'financeiro', didactic: 'pedagogico', all: 'all', both: 'all' };
      const { data } = await api.post('/dnachat/whatsapp/broadcast/preview', {
        text: body.trim() || undefined,
        class_ids: Array.from(selectedClassIds),
        guardian_type: gtMap[guardianType] || 'all',
        send_mode: sendMode,
      });
      setWaPreview(data);
      setApprovedTemplates(data.templates || []);
      // Se veio template previamente escolhido e ainda aprovado, mantém; senão limpa
      if (tplName) {
        const still = (data.templates || []).some((t) => t.name === tplName && (t.status || '').toUpperCase() === 'APPROVED');
        if (!still) { setTplName(''); setTplVarMap({}); }
      }
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar prévia do WhatsApp.');
      return null;
    }
  };

  const goToTemplateStep = async () => {
    if (!alsoWhatsApp) {
      // Sem WA: pula direto para envio (rota DNA interna)
      return submit(false);
    }
    setSending(false);
    const p = await loadWaPreview();
    if (p) setStep(3);
  };

  const syncTemplatesFromMeta = async () => {
    setSyncingTpl(true);
    try {
      const { data } = await api.post('/dnachat/whatsapp/templates/sync');
      toast.success(`Sincronizado com Meta — ${data?.count || 0} templates.`);
      await loadWaPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível sincronizar templates.');
    } finally { setSyncingTpl(false); }
  };

  // Deriva componentes/variáveis do template escolhido
  const tplDoc = approvedTemplates.find((t) => t.name === tplName && (!tplLang || t.language === tplLang));
  const tplBodyText = (() => {
    const c = (tplDoc?.components || []).find((x) => (x.type || '').toUpperCase() === 'BODY');
    return c?.text || '';
  })();
  const tplVarIndices = (() => {
    const set = new Set();
    (tplBodyText.match(/\{\{(\d+)\}\}/g) || []).forEach((m) => {
      const i = parseInt(m.replace(/[^0-9]/g, ''), 10);
      if (!Number.isNaN(i)) set.add(i);
    });
    return Array.from(set).sort((a, b) => a - b);
  })();
  const varMissing = tplVarIndices.some((i) => !((tplVarMap[String(i)] || '').trim()));

  const renderTemplateForSample = (recipient) => {
    if (!tplBodyText) return '';
    return tplBodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
      const spec = (tplVarMap[String(n)] || '').trim();
      if (!spec) return `{{${n}}}`;
      if (spec === 'guardian_name') return recipient?.responsible_name || recipient?.guardian_name || '';
      if (spec === 'student_name') return (recipient?.students?.[0]?.name) || recipient?.student_name || '';
      if (spec === 'class_name') return (recipient?.students?.[0]?.class_name) || recipient?.class_name || '';
      if (spec.startsWith('text:')) return spec.slice(5);
      if (spec.startsWith('custom:')) return spec.slice(7);
      return recipient?.[spec] || '';
    });
  };

  const templateRequired = !!(waPreview && (waPreview.window_closed > 0 || sendMode === 'template_all'));
  const canSendWA = !alsoWhatsApp || (!templateRequired) || (tplName && !varMissing);

  const submit = async (saveOnly = false) => {
    if (sending) return;
    if (!saveOnly && !body.trim() && !tplName) return toast.error('Escreva a mensagem ou escolha um template.');
    if (alsoWhatsApp && templateRequired && !tplName) {
      return toast.error('Selecione um template aprovado antes de enviar.');
    }
    if (alsoWhatsApp && tplName && varMissing) {
      return toast.error('Preencha todas as variáveis do template.');
    }
    setSending(true);
    try {
      // 1) DNA interno (opcional, apenas save-list ou envio in-app)
      const { data } = await api.post('/dnachat/broadcast/create', {
        name,
        class_ids: Array.from(selectedClassIds),
        guardian_type: guardianType,
        exclude_user_ids: Array.from(excluded),
        body: saveOnly ? null : (body.trim() || null),
        save_only: saveOnly,
      });
      toast.success(saveOnly
        ? `Lista "${name}" salva — ${data.n_recipients} destinatário(s).`
        : `Lista registrada — ${data.n_sent} destinatário(s).`);
      // 2) WhatsApp em paralelo (com template + modo)
      if (!saveOnly && alsoWhatsApp) {
        try {
          const gtMap = { financial: 'financeiro', didactic: 'pedagogico', all: 'all', both: 'all' };
          const wa = await api.post('/dnachat/whatsapp/broadcast/send', {
            text: body.trim() || undefined,
            class_ids: Array.from(selectedClassIds),
            guardian_type: gtMap[guardianType] || 'all',
            send_mode: sendMode,
            template_name: tplName || undefined,
            template_language: tplLang || 'pt_BR',
            template_variables_map: tplName ? tplVarMap : undefined,
          });
          toast.success(`WhatsApp: ${wa?.data?.queued || 0} enfileirados · janela ativa ${wa?.data?.window_active || 0} · template ${wa?.data?.window_closed || 0}`);
        } catch (wErr) {
          const d = wErr?.response?.data?.detail;
          const code = typeof d === 'object' ? d?.code : null;
          if (code === 'TEMPLATE_REQUIRED' || code === 'TEMPLATE_NOT_APPROVED') {
            toast.error(typeof d === 'object' ? d.message : 'Selecione um template aprovado.');
          } else {
            toast.error(`WhatsApp falhou: ${typeof d === 'string' ? d : JSON.stringify(d || wErr?.message).slice(0, 200)}`);
          }
          setSending(false);
          return;
        }
      }
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível salvar a lista.');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dnachat-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5" />Nova lista de transmissão</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <>
            <Input placeholder="Nome da lista (ex.: Aviso 5º ano)" value={name} onChange={(e) => setName(e.target.value)} data-testid="dnachat-broadcast-name" />
            <div className="grid grid-cols-3 gap-2 text-xs">
              <label className="flex items-center gap-1 border border-zinc-200 rounded p-1.5 cursor-pointer">
                <input type="radio" checked={guardianType === 'all'} onChange={() => setGuardianType('all')} data-testid="dnachat-guardian-all" /> Todos responsáveis
              </label>
              <label className="flex items-center gap-1 border border-zinc-200 rounded p-1.5 cursor-pointer">
                <input type="radio" checked={guardianType === 'financial'} onChange={() => setGuardianType('financial')} data-testid="dnachat-guardian-fin" /> Só financeiro
              </label>
              <label className="flex items-center gap-1 border border-zinc-200 rounded p-1.5 cursor-pointer">
                <input type="radio" checked={guardianType === 'didactic'} onChange={() => setGuardianType('didactic')} data-testid="dnachat-guardian-did" /> Só didático
              </label>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input placeholder="Buscar turma…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="dnachat-class-search" />
              </div>
              <Button size="sm" variant="outline" onClick={selectAllFiltered} data-testid="dnachat-select-all-classes">Marcar todas ({filteredClasses.length})</Button>
              <Button size="sm" variant="ghost" onClick={clearAll}>Limpar</Button>
            </div>
            <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded">
              {loading && <div className="p-4 text-center text-xs text-zinc-400">Carregando turmas reais…</div>}
              {!loading && filteredClasses.length === 0 && <div className="p-4 text-center text-xs text-zinc-400">Nenhuma turma encontrada</div>}
              {filteredClasses.map((c) => {
                const sel = selectedClassIds.has(c.id);
                return (
                  <label key={c.id} className={`px-3 py-2 border-b border-zinc-100 cursor-pointer flex items-center gap-3 hover:bg-zinc-50 ${sel ? 'bg-indigo-50' : ''}`}
                    data-testid={`dnachat-class-${c.id.slice(0, 8)}`}>
                    <input type="checkbox" checked={sel} onChange={() => toggleClass(c.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{c.name || 'Turma'}</div>
                      <div className="text-[10px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {c.segment && <span>{c.segment}</span>}
                        <span><b>{c.student_count || 0}</b> alunos</span>
                        <span><b>{c.guardian_count || 0}</b> responsáveis</span>
                        {(c.financial_count > 0 || c.didactic_count > 0) && (
                          <span className="text-emerald-700">
                            {c.financial_count || 0} fin · {c.didactic_count || 0} did
                          </span>
                        )}
                        {c.without_phone_count > 0 && (
                          <span className="text-amber-700">{c.without_phone_count} sem tel</span>
                        )}
                      </div>
                    </div>
                    {sel && <Check className="w-4 h-4 text-indigo-600" />}
                  </label>
                );
              })}
            </div>
            <div className="text-xs text-zinc-500">
              {selectedClassIds.size} turma(s) selecionada(s)
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={resolve} disabled={loading || selectedClassIds.size === 0 || !name.trim()} className="bg-indigo-600 text-white" data-testid="dnachat-broadcast-resolve">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Próximo · Ver destinatários
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 2 && resolved && (
          <>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-[11px]" data-testid="dnachat-broadcast-summary">
              <div className="rounded bg-slate-50 p-2 border border-slate-200"><div className="text-[9px] uppercase text-slate-500">Turmas</div><div className="font-bold text-slate-800">{resolved.n_classes}</div></div>
              <div className="rounded bg-slate-50 p-2 border border-slate-200"><div className="text-[9px] uppercase text-slate-500">Alunos</div><div className="font-bold text-slate-800">{resolved.n_students}</div></div>
              <div className="rounded bg-slate-50 p-2 border border-slate-200"><div className="text-[9px] uppercase text-slate-500">Responsáveis</div><div className="font-bold text-slate-800">{resolved.n_responsibles_found}</div></div>
              <div className="rounded bg-emerald-50 p-2 border border-emerald-200"><div className="text-[9px] uppercase text-emerald-700">Telefones válidos</div><div className="font-bold text-emerald-800">{resolved.n_valid_phones}</div></div>
              <div className="rounded bg-amber-50 p-2 border border-amber-200"><div className="text-[9px] uppercase text-amber-700">Duplicados</div><div className="font-bold text-amber-800">{resolved.n_duplicated_phones}</div></div>
              <div className="rounded bg-rose-50 p-2 border border-rose-200"><div className="text-[9px] uppercase text-rose-700">Sem celular</div><div className="font-bold text-rose-800">{resolved.n_no_phone}</div></div>
            </div>
            <div className="max-h-56 overflow-y-auto border border-zinc-200 rounded text-xs">
              {resolved.recipients.length === 0 && <div className="p-3 text-center text-zinc-400">Nenhum destinatário com telefone válido</div>}
              {resolved.recipients.map((r) => {
                const isExcluded = excluded.has(r.user_id || r.normalized_phone);
                const key = r.user_id || r.normalized_phone;
                const gt = r.guardian_type;
                const gtLabel = gt === 'both' ? 'Financeiro + Didático' : gt === 'financial' ? 'Financeiro' : gt === 'didactic' ? 'Didático' : 'Outro';
                const gtColor = gt === 'both' ? 'bg-purple-100 text-purple-800' : gt === 'financial' ? 'bg-emerald-100 text-emerald-800' : gt === 'didactic' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700';
                return (
                  <div key={key} className={`px-3 py-1.5 border-b border-zinc-100 flex items-center gap-3 ${isExcluded ? 'opacity-40' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-zinc-800 truncate">{r.responsible_name}</div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${gtColor}`}>{gtLabel}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {r.students?.map((s) => `${s.name}${s.class_name ? ` · ${s.class_name}` : ''}`).join(' | ')}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-600 flex items-center gap-2">
                        {r.phone}
                        {r.whatsapp_url && (
                          <a href={r.whatsapp_url} target="_blank" rel="noreferrer" className="text-[9px] px-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200">WA</a>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setExcluded((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; })}
                      className="text-[10px] text-rose-600 hover:underline"
                    >
                      {isExcluded ? 'Reincluir' : 'Remover'}
                    </button>
                  </div>
                );
              })}
            </div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Mensagem que será enviada individualmente a cada destinatário…"
              rows={4}
              data-testid="dnachat-broadcast-body"
            />
            <label className="flex items-center gap-2 text-xs text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="dnachat-also-whatsapp-label">
              <input type="checkbox" checked={alsoWhatsApp} onChange={(e) => setAlsoWhatsApp(e.target.checked)} data-testid="dnachat-also-whatsapp" />
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              <span><b>Também enviar pelo WhatsApp</b> — dispara em paralelo via WhatsApp Business (respeita opt-out e valida número).</span>
            </label>
            <div className="text-[11px] text-zinc-500">
              Total final: <b>{resolved.n_valid_phones - excluded.size}</b> destinatário(s) únicos.
              Cada um recebe uma conversa direta — nenhum vê os demais contatos.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} disabled={sending}>Voltar</Button>
              <Button variant="outline" onClick={() => submit(true)} disabled={sending}>Salvar lista</Button>
              <Button
                onClick={goToTemplateStep}
                disabled={sending || (!body.trim() && !alsoWhatsApp)}
                className="bg-indigo-600 text-white"
                data-testid="dnachat-broadcast-next-template"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                {alsoWhatsApp ? 'Próximo · Template WhatsApp' : 'Enviar (somente DNA)'}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* V4.76 · Etapa 3 · Template WhatsApp + Modo + Variáveis */}
        {step === 3 && waPreview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-[11px]" data-testid="dnachat-wa-window-summary">
              <div className="rounded bg-emerald-50 p-2 border border-emerald-200"><div className="text-[9px] uppercase text-emerald-700">Válidos únicos</div><div className="font-bold text-emerald-800">{waPreview.valid_phones}</div></div>
              <div className="rounded bg-sky-50 p-2 border border-sky-200"><div className="text-[9px] uppercase text-sky-700">Janela ativa</div><div className="font-bold text-sky-800">{waPreview.window_active}</div></div>
              <div className="rounded bg-amber-50 p-2 border border-amber-200"><div className="text-[9px] uppercase text-amber-700">Precisam template</div><div className="font-bold text-amber-800">{waPreview.window_closed}</div></div>
              <div className="rounded bg-rose-50 p-2 border border-rose-200"><div className="text-[9px] uppercase text-rose-700">Sem WhatsApp</div><div className="font-bold text-rose-800">{waPreview.no_phone}</div></div>
            </div>
            {templateRequired && (
              <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded px-3 py-2" data-testid="dnachat-wa-template-required">
                <b>{waPreview.window_closed}</b> {waPreview.window_closed === 1 ? 'família precisa' : 'famílias precisam'} receber um template aprovado.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className={`flex items-start gap-2 border rounded p-2 cursor-pointer ${sendMode === 'auto' ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200'}`}>
                <input type="radio" name="sendMode" checked={sendMode === 'auto'} onChange={() => setSendMode('auto')} data-testid="dnachat-mode-auto" />
                <div>
                  <div className="font-semibold text-zinc-800">Automático · recomendado</div>
                  <div className="text-[10px] text-zinc-500">Janela ativa → texto livre. Janela fechada → template selecionado.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 border rounded p-2 cursor-pointer ${sendMode === 'template_all' ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200'}`}>
                <input type="radio" name="sendMode" checked={sendMode === 'template_all'} onChange={() => setSendMode('template_all')} data-testid="dnachat-mode-tpl-all" />
                <div>
                  <div className="font-semibold text-zinc-800">Template para todos</div>
                  <div className="text-[10px] text-zinc-500">Todos os destinatários recebem o template escolhido.</div>
                </div>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Template WhatsApp</div>
                <select
                  className="w-full border border-zinc-300 rounded px-2 py-2 text-sm bg-white"
                  value={tplName ? `${tplName}::${tplLang}` : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) { setTplName(''); setTplLang('pt_BR'); setTplVarMap({}); return; }
                    const [n, l] = v.split('::');
                    setTplName(n); setTplLang(l || 'pt_BR'); setTplVarMap({});
                  }}
                  data-testid="dnachat-tpl-select"
                >
                  <option value="">— Selecionar template aprovado —</option>
                  {approvedTemplates.map((t) => (
                    <option key={`${t.name}::${t.language}`} value={`${t.name}::${t.language}`}>
                      {t.name} · {t.category || 'UTILITY'} · {t.language}
                    </option>
                  ))}
                </select>
              </div>
              <Button variant="outline" size="sm" onClick={syncTemplatesFromMeta} disabled={syncingTpl} data-testid="dnachat-tpl-sync">
                {syncingTpl ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Atualizar templates da Meta
              </Button>
            </div>
            {approvedTemplates.length === 0 && (
              <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2" data-testid="dnachat-tpl-empty">
                Nenhum template <b>APPROVED</b> disponível.
                <a href="/v2/chat/whatsapp/templates" className="ml-1 underline">Ir para Templates</a>
              </div>
            )}
            {tplDoc && (
              <div className="border border-zinc-200 rounded bg-zinc-50 p-2 text-xs" data-testid="dnachat-tpl-preview">
                <div className="text-[10px] uppercase text-zinc-500">Prévia · {tplDoc.category || 'UTILITY'} · {tplDoc.language}</div>
                <pre className="whitespace-pre-wrap font-sans text-[12px] text-zinc-800 mt-1">{tplBodyText}</pre>
              </div>
            )}
            {tplVarIndices.length > 0 && (
              <div className="border border-zinc-200 rounded p-2" data-testid="dnachat-tpl-vars">
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Variáveis · escolha o valor por destinatário</div>
                <div className="grid grid-cols-1 gap-2">
                  {tplVarIndices.map((i) => {
                    const spec = tplVarMap[String(i)] || '';
                    const isText = spec.startsWith('text:');
                    const isCustom = spec.startsWith('custom:');
                    const preset = isText ? 'text' : isCustom ? 'custom' : spec;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-mono text-zinc-500 w-14 shrink-0">{`{{${i}}}`}</span>
                        <select
                          className="border border-zinc-300 rounded px-2 py-1 bg-white text-xs"
                          value={preset}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTplVarMap((prev) => ({ ...prev, [String(i)]: v === 'text' ? 'text:' : (v === 'custom' ? 'custom:' : v) }));
                          }}
                          data-testid={`dnachat-tpl-var-${i}-key`}
                        >
                          <option value="">— escolher —</option>
                          <option value="guardian_name">Nome do responsável</option>
                          <option value="student_name">Nome do aluno</option>
                          <option value="class_name">Turma</option>
                          <option value="text">Texto fixo (mesmo para todos)</option>
                          <option value="custom">Personalizado por destinatário</option>
                        </select>
                        {(isText || isCustom) && (
                          <input
                            className="flex-1 border border-zinc-300 rounded px-2 py-1 text-xs"
                            placeholder={isText ? 'Texto igual para todos' : 'Ex.: nome da unidade'}
                            value={isText ? spec.slice(5) : spec.slice(7)}
                            onChange={(e) => setTplVarMap((prev) => ({ ...prev, [String(i)]: (isText ? 'text:' : 'custom:') + e.target.value }))}
                            data-testid={`dnachat-tpl-var-${i}-val`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)} disabled={sending}>Voltar</Button>
              <Button
                onClick={() => setStep(4)}
                disabled={!canSendWA || (sendMode === 'template_all' && !tplName)}
                className="bg-indigo-600 text-white"
                data-testid="dnachat-broadcast-next-review"
              >
                Continuar · Revisar
              </Button>
            </DialogFooter>
          </>
        )}

        {/* V4.76 · Etapa 4 · Prévia individual + Resumo + Envio */}
        {step === 4 && waPreview && (
          <>
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3" data-testid="dnachat-broadcast-review">
              <div className="font-semibold text-slate-800 mb-1">Transmissão</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-700">
                <div>Lista: <b>{name}</b></div>
                <div>Turmas: <b>{selectedClassIds.size}</b></div>
                <div>Famílias válidas: <b>{waPreview.valid_phones}</b></div>
                <div>Janela ativa: <b>{waPreview.window_active}</b></div>
                <div>Template: <b>{waPreview.window_closed}</b></div>
                <div>Sem WhatsApp: <b>{waPreview.no_phone}</b></div>
                <div>Duplicados removidos: <b>{waPreview.duplicates_removed}</b></div>
                <div>Modo: <b>{sendMode === 'template_all' ? 'Template para todos' : 'Automático'}</b></div>
                <div>Template: <b>{tplName || '—'}</b>{tplName ? ` · ${tplLang}` : ''}</div>
              </div>
            </div>
            {tplName && resolved?.recipients?.[0] && (
              <div className="border border-zinc-200 rounded p-2 bg-white" data-testid="dnachat-preview-individual">
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Prévia individual · {resolved.recipients[0].responsible_name}</div>
                <pre className="whitespace-pre-wrap font-sans text-[12px] text-zinc-800">{renderTemplateForSample(resolved.recipients[0])}</pre>
              </div>
            )}
            {body.trim() && sendMode === 'auto' && waPreview.window_active > 0 && (
              <div className="border border-emerald-200 rounded p-2 bg-emerald-50" data-testid="dnachat-preview-text">
                <div className="text-[10px] uppercase text-emerald-700 mb-1">Texto livre · janela ativa</div>
                <pre className="whitespace-pre-wrap font-sans text-[12px] text-zinc-800">{body.trim()}</pre>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(3)} disabled={sending}>Voltar</Button>
              <Button
                onClick={() => submit(false)}
                disabled={sending || !canSendWA}
                className="bg-emerald-600 text-white"
                data-testid="dnachat-broadcast-send-final"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar para {waPreview.valid_phones - excluded.size} {waPreview.valid_phones - excluded.size === 1 ? 'família' : 'famílias'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────  NOVA CONVERSA  ────────────────────────
function NewConversationDialog({ open, onClose, onCreated, type = 'direct' }) {
  const [mode, setMode] = useState('families'); // families | contacts | class
  const [q, setQ] = useState('');
  const [contacts, setContacts] = useState([]);
  const [families, setFamilies] = useState([]);
  const [gtypeFilter, setGtypeFilter] = useState(''); // '' | financial | pedagogical | both
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  // modo turma → aluno → responsável
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [responsibles, setResponsibles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'families') {
      setLoading(true);
      api.get('/dnachat/families', { params: { q: q || undefined, guardian_type: gtypeFilter || undefined } })
        .then((r) => setFamilies(r.data?.families || []))
        .catch(() => setFamilies([]))
        .finally(() => setLoading(false));
    } else if (mode === 'contacts') {
      setLoading(true);
      api.get('/dnachat/directory', { params: { q } })
        .then((r) => setContacts(r.data?.contacts || []))
        .finally(() => setLoading(false));
    } else if (mode === 'class') {
      api.get('/dnachat/broadcast/classes').then((r) => setClasses(r.data?.classes || []));
    }
  }, [open, q, mode, gtypeFilter]);

  useEffect(() => {
    if (!selectedClass) { setStudents([]); setSelectedStudent(null); setResponsibles([]); return; }
    api.get(`/dnachat/classes/${selectedClass.id}/students`).then((r) => setStudents(r.data?.students || []));
    setSelectedStudent(null); setResponsibles([]);
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedStudent) { setResponsibles([]); return; }
    api.get(`/dnachat/students/${selectedStudent.id}/responsibles`).then((r) => setResponsibles(r.data?.responsibles || []));
  }, [selectedStudent]);

  const toggle = (c) => {
    if (type === 'direct') {
      setSelected([c]);
    } else {
      setSelected((prev) => prev.find((x) => x.id === c.id) ? prev.filter((x) => x.id !== c.id) : [...prev, c]);
    }
  };

  const createDirect = async (userId, displayInfo) => {
    if (!userId) return toast.error('Este responsável não possui conta no DNACore para receber mensagens no chat.');
    try {
      const { data } = await api.post('/dnachat/conversations', { type: 'direct', member_ids: [userId] });
      onCreated?.({ ...data, ...(displayInfo || {}) });
      onClose();
      setSelected([]); setName(''); setQ(''); setMode('contacts');
      setSelectedClass(null); setSelectedStudent(null); setResponsibles([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao criar');
    }
  };

  const create = async () => {
    if (selected.length === 0) return;
    try {
      const { data } = await api.post('/dnachat/conversations', {
        type,
        name: type === 'direct' ? undefined : (name || undefined),
        member_ids: selected.map((s) => s.id),
      });
      onCreated?.(data);
      onClose();
      setSelected([]); setName(''); setQ('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao criar');
    }
  };

  const label = { direct: 'Nova conversa', group: 'Novo grupo', community: 'Nova comunidade', broadcast: 'Lista de transmissão' }[type];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dnachat-new-dialog">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>
        {type === 'direct' && (
          <div className="grid grid-cols-3 border border-zinc-200 rounded overflow-hidden text-xs">
            <button
              className={`py-1.5 ${mode === 'families' ? 'bg-emerald-600 text-white' : 'bg-white hover:bg-zinc-50'}`}
              onClick={() => setMode('families')}
              data-testid="dnachat-mode-families"
            >Famílias</button>
            <button
              className={`py-1.5 ${mode === 'contacts' ? 'bg-indigo-600 text-white' : 'bg-white hover:bg-zinc-50'}`}
              onClick={() => setMode('contacts')}
              data-testid="dnachat-mode-contacts"
            >Colaboradores</button>
            <button
              className={`py-1.5 ${mode === 'class' ? 'bg-indigo-600 text-white' : 'bg-white hover:bg-zinc-50'}`}
              onClick={() => setMode('class')}
              data-testid="dnachat-mode-class"
            >Turma → Aluno</button>
          </div>
        )}
        {type !== 'direct' && (
          <Input placeholder="Nome do grupo/lista" value={name} onChange={(e) => setName(e.target.value)} data-testid="dnachat-new-name" />
        )}

        {/* V4.69 · Famílias reais (guardian + alunos + turmas + WhatsApp) */}
        {mode === 'families' && type === 'direct' && (
          <>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input placeholder="Buscar por responsável, aluno, turma ou telefone…" value={q}
                  onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="dnachat-family-search" />
              </div>
              <select
                value={gtypeFilter}
                onChange={(e) => setGtypeFilter(e.target.value)}
                className="text-xs border border-zinc-200 rounded px-2"
                data-testid="dnachat-family-gtype-filter"
              >
                <option value="">Todos</option>
                <option value="financial">Financeiro</option>
                <option value="pedagogical">Didático</option>
                <option value="both">Ambos</option>
              </select>
            </div>
            <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded" data-testid="dnachat-family-list">
              {loading && <div className="p-4 text-center text-xs text-zinc-400">Carregando famílias…</div>}
              {!loading && families.length === 0 && (
                <div className="p-4 text-center text-xs text-zinc-400">Nenhuma família encontrada.</div>
              )}
              {families.map((f) => (
                <div
                  key={f.guardian_id}
                  onClick={() => createDirect(f.user_id || f.guardian_id, {
                    display_name: f.guardian_name, guardian_phone: f.normalized_phone,
                    responsible_id: f.guardian_id, normalized_phone: f.normalized_phone,
                  })}
                  className="px-3 py-2 border-b border-zinc-100 cursor-pointer hover:bg-emerald-50 flex items-start gap-3"
                  data-testid={`dnachat-family-${f.guardian_id.slice(0, 8)}`}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                    {(f.guardian_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-800 truncate flex items-center gap-1">
                      {f.guardian_name}
                      {f.whatsapp_available && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">WA</span>}
                    </div>
                    <div className="text-[10px] text-zinc-600">
                      {f.guardian_type === 'both' && 'Responsável financeiro e didático'}
                      {f.guardian_type === 'financial' && 'Responsável financeiro'}
                      {f.guardian_type === 'pedagogical' && 'Responsável didático'}
                    </div>
                    {(f.students || []).length > 0 && (
                      <div className="text-[10px] text-zinc-600 mt-0.5">
                        {(f.students || []).slice(0, 3).map((s, i) => (
                          <div key={s.student_id}>
                            {i === 0 && (f.students.length > 1 ? 'Alunos: ' : 'Aluno: ')}
                            <b>{s.student_name}</b>{s.class_name ? ` — ${s.class_name}` : ''}
                          </div>
                        ))}
                        {(f.students || []).length > 3 && (
                          <div className="text-zinc-400">+{f.students.length - 3} outro(s)</div>
                        )}
                      </div>
                    )}
                    {f.phone && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">WhatsApp: {f.phone}</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {(mode === 'contacts' || type !== 'direct') && (
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input placeholder="Buscar contato…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="dnachat-new-search" />
            </div>
            <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded">
              {loading && <div className="p-4 text-center text-xs text-zinc-400">Carregando…</div>}
              {contacts.filter((c) => c && c.id).map((c) => {
                const sel = selected.find((x) => x.id === c.id);
                return (
                  <div
                    key={`${c.entity_type}-${c.id}`}
                    onClick={() => toggle(c)}
                    className={`px-3 py-2 border-b border-zinc-100 cursor-pointer flex items-center gap-3 hover:bg-zinc-50 ${sel ? 'bg-indigo-50' : ''}`}
                    data-testid={`dnachat-new-contact-${c.id.slice(0, 8)}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                      {(c.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                      <div className="text-[10px] text-zinc-500 flex gap-2">
                        <Badge variant="outline" className="px-1 py-0 text-[9px]">{c.entity_type}</Badge>
                        {c.email && <span className="truncate">{c.email}</span>}
                      </div>
                    </div>
                    {sel && <Check className="w-4 h-4 text-indigo-600" />}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={create} disabled={selected.length === 0} className="bg-indigo-600 text-white" data-testid="dnachat-new-create">
                {type === 'direct' ? `Iniciar (${selected.length})` : `Criar (${selected.length})`}
              </Button>
            </DialogFooter>
          </>
        )}

        {mode === 'class' && type === 'direct' && (
          <ClassMultiSelectFlow
            classes={classes}
            createDirect={createDirect}
            selectedClass={selectedClass}
            setSelectedClass={setSelectedClass}
            students={students}
            setStudents={setStudents}
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
            responsibles={responsibles}
            setResponsibles={setResponsibles}
            onClose={onClose}
          />
        )}
        {mode === 'class' && type === 'direct' && false && (
          <div className="space-y-3">
            {/* Escolher turma */}
            <div>
              <div className="text-[11px] font-semibold text-zinc-600 mb-1">1. Escolha a turma</div>
              <select
                className="w-full border border-zinc-200 rounded px-2 py-1.5 text-sm"
                value={selectedClass?.id || ''}
                onChange={(e) => {
                  const c = classes.find((x) => x.id === e.target.value);
                  setSelectedClass(c || null);
                }}
                data-testid="dnachat-conv-class-select"
              >
                <option value="">— selecionar —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.student_count || 0} alunos · {c.guardian_count || 0} resps</option>
                ))}
              </select>
            </div>
            {/* Escolher aluno */}
            {selectedClass && (
              <div>
                <div className="text-[11px] font-semibold text-zinc-600 mb-1">2. Escolha o aluno ({students.length})</div>
                <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded">
                  {students.length === 0 && <div className="p-3 text-xs text-zinc-400 text-center">Nenhum aluno ativo</div>}
                  {students.map((s) => (
                    <div
                      key={s.id}
                      onClick={() => setSelectedStudent(s)}
                      className={`px-3 py-1.5 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 ${selectedStudent?.id === s.id ? 'bg-indigo-50' : ''}`}
                      data-testid={`dnachat-conv-student-${s.id.slice(0, 8)}`}
                    >
                      <div className="text-sm font-medium">{s.name}</div>
                      {s.registration_number && <div className="text-[10px] text-zinc-500">RM: {s.registration_number}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Escolher responsável */}
            {selectedStudent && (
              <div>
                <div className="text-[11px] font-semibold text-zinc-600 mb-1">3. Escolha o responsável ({responsibles.length})</div>
                <div className="max-h-56 overflow-y-auto space-y-2">
                  {responsibles.length === 0 && <div className="p-3 text-xs text-zinc-400 text-center border border-zinc-200 rounded">Nenhum responsável vinculado a este aluno</div>}
                  {responsibles.map((r) => {
                    const gt = r.guardian_type;
                    const gtLabel = gt === 'both' ? 'Financeiro + Didático' : gt === 'financial' ? 'Financeiro' : gt === 'didactic' ? 'Didático' : 'Outro';
                    const gtColor = gt === 'both' ? 'bg-purple-100 text-purple-800' : gt === 'financial' ? 'bg-emerald-100 text-emerald-800' : gt === 'didactic' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700';
                    return (
                      <div key={r.responsible_id} className="border border-zinc-200 rounded p-2" data-testid={`dnachat-conv-resp-${r.responsible_id.slice(0, 8)}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-semibold">{r.responsible_name}</div>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${gtColor}`}>{gtLabel}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500">
                              Responsável por {selectedStudent.name} · {selectedStudent.class_name}
                            </div>
                            <div className="text-[10px] font-mono text-zinc-600">{r.phone || 'sem telefone'}</div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              className="text-[11px] h-6 bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => createDirect(r.user_id, { responsible_name: r.responsible_name, phone: r.phone, normalized_phone: r.normalized_phone, whatsapp_url: r.whatsapp_url, students: [r.student] })}
                              disabled={!r.user_id}
                            >Iniciar conversa</Button>
                            {r.whatsapp_url && (
                              <a
                                href={r.whatsapp_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                              >Abrir WA</a>
                            )}
                          </div>
                        </div>
                        {!r.user_id && (
                          <div className="text-[9px] text-amber-700 mt-1">⚠️ Este responsável ainda não tem conta no DNACore — apenas contato por WhatsApp está disponível.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Fechar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────────  V4.72 · TURMA → ALUNO → RESPONSÁVEL COM MULTISSELEÇÃO  ────────────────────────
function ClassMultiSelectFlow({ classes, createDirect, onClose }) {
  const [selectedClassIds, setSelectedClassIds] = React.useState([]);
  const [classSearch, setClassSearch] = React.useState('');
  // V4.73 · Passo intermediário: alunos específicos das turmas selecionadas
  const [students, setStudents] = React.useState([]);
  const [studentSearch, setStudentSearch] = React.useState('');
  const [selectedStudentIds, setSelectedStudentIds] = React.useState([]); // [] = todos
  const [studentPickerOpen, setStudentPickerOpen] = React.useState(false);
  const [families, setFamilies] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedGuardianIds, setSelectedGuardianIds] = React.useState([]);
  const [gtypeFilter, setGtypeFilter] = React.useState('');
  const [bulkText, setBulkText] = React.useState('');
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const filteredClasses = React.useMemo(() =>
    classes.filter((c) => (c.name || '').toLowerCase().includes(classSearch.toLowerCase())),
    [classes, classSearch]);

  const toggleClass = (id) => setSelectedClassIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const selectAllClasses = () => setSelectedClassIds(filteredClasses.map((c) => c.id));
  const clearClasses = () => setSelectedClassIds([]);

  React.useEffect(() => {
    if (selectedClassIds.length === 0) { setStudents([]); setSelectedStudentIds([]); setFamilies([]); setSelectedGuardianIds([]); return; }
    // V4.73 · Carrega alunos das turmas (para picker opcional)
    api.get('/dnachat/students/by-classes', { params: { class_ids: selectedClassIds.join(',') } })
      .then((r) => setStudents(r.data?.students || []))
      .catch(() => setStudents([]));
  }, [selectedClassIds]);

  React.useEffect(() => {
    if (selectedClassIds.length === 0) { setFamilies([]); setSelectedGuardianIds([]); return; }
    setLoading(true);
    const params = { class_ids: selectedClassIds.join(','), guardian_type: gtypeFilter || undefined };
    if (selectedStudentIds.length > 0) params.student_ids = selectedStudentIds.join(',');
    api.get('/dnachat/families', { params })
      .then((r) => {
        const fams = r.data?.families || [];
        setFamilies(fams);
        setSelectedGuardianIds(fams.map((f) => f.guardian_id));
      })
      .catch(() => setFamilies([]))
      .finally(() => setLoading(false));
  }, [selectedClassIds, selectedStudentIds, gtypeFilter]);

  const toggleGuardian = (id) => setSelectedGuardianIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const selectedFamilies = families.filter((f) => selectedGuardianIds.includes(f.guardian_id));
  const withWA = selectedFamilies.filter((f) => f.whatsapp_available);

  const iniciarConversas = async () => {
    setBulkBusy(true); let ok = 0;
    for (const f of selectedFamilies) {
      try {
        await createDirect(f.user_id || f.guardian_id, {
          display_name: f.guardian_name, guardian_phone: f.normalized_phone,
          responsible_id: f.guardian_id, normalized_phone: f.normalized_phone,
        });
        ok++;
      } catch (_) { /* segue */ }
    }
    toast.success(`${ok} conversas iniciadas/reabertas`);
    setBulkBusy(false); onClose();
  };

  const enviarMensagem = async () => {
    if (!bulkText.trim()) return toast.error('Digite a mensagem');
    setBulkBusy(true);
    try {
      const { data } = await api.post('/dnachat/whatsapp/broadcast/send', {
        text: bulkText.trim(), guardian_ids: selectedGuardianIds,
      });
      toast.success(`Enviado · ${data?.queued || 0} destinatários · broadcast ${String(data?.broadcast_id || '').slice(0, 8)}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao disparar transmissão');
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] font-semibold text-zinc-600 mb-1 flex items-center justify-between">
          <span>1. Turmas ({selectedClassIds.length} selecionadas)</span>
          <div className="flex gap-1">
            <button onClick={selectAllClasses} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100">Todas</button>
            <button onClick={clearClasses} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200">Limpar</button>
          </div>
        </div>
        <Input placeholder="Buscar turma…" value={classSearch} onChange={(e) => setClassSearch(e.target.value)} className="mb-1" data-testid="dnachat-class-search" />
        <div className="max-h-36 overflow-y-auto border border-zinc-200 rounded">
          {filteredClasses.map((c) => (
            <label key={c.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-zinc-50 cursor-pointer border-b border-zinc-100" data-testid={`dnachat-class-opt-${c.id.slice(0, 8)}`}>
              <input type="checkbox" checked={selectedClassIds.includes(c.id)} onChange={() => toggleClass(c.id)} />
              <span className="flex-1">{c.name}</span>
              <span className="text-[10px] text-zinc-400">{c.student_count || 0} alunos</span>
            </label>
          ))}
          {filteredClasses.length === 0 && <div className="p-3 text-center text-xs text-zinc-400">Nenhuma turma</div>}
        </div>
      </div>

      {selectedClassIds.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-zinc-600 mb-1 flex items-center justify-between">
            <span>2. Alunos {selectedStudentIds.length > 0 ? `(${selectedStudentIds.length} filtrados)` : `(todos · ${students.length})`}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => setStudentPickerOpen((v) => !v)}
                className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100" data-testid="dnachat-student-picker-toggle">
                {studentPickerOpen ? 'Ocultar' : (selectedStudentIds.length > 0 ? 'Editar' : 'Escolher alunos específicos')}
              </button>
              {selectedStudentIds.length > 0 && (
                <button type="button" onClick={() => setSelectedStudentIds([])}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200">Limpar</button>
              )}
            </div>
          </div>
          {studentPickerOpen && (
            <div className="border border-zinc-200 rounded p-1.5" data-testid="dnachat-student-picker">
              <Input placeholder="Buscar aluno…" value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} className="mb-1" data-testid="dnachat-student-search" />
              <div className="flex gap-1 mb-1">
                <button type="button" onClick={() => setSelectedStudentIds(students.map((s) => s.id))} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100">Todos</button>
                <button type="button" onClick={() => setSelectedStudentIds([])} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200">Limpar</button>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {students.filter((s) => (s.name || '').toLowerCase().includes(studentSearch.toLowerCase())).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-1.5 py-1 text-[11px] hover:bg-zinc-50 cursor-pointer border-b border-zinc-100" data-testid={`dnachat-student-opt-${s.id.slice(0, 8)}`}>
                    <input type="checkbox" checked={selectedStudentIds.includes(s.id)}
                      onChange={() => setSelectedStudentIds((cur) => cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id])} />
                    <span className="flex-1">{s.name} <span className="text-zinc-400">· {s.class_name}</span></span>
                    {s.rm && <span className="text-[9px] font-mono text-zinc-400">RM {s.rm}</span>}
                  </label>
                ))}
                {students.length === 0 && <div className="p-2 text-center text-[11px] text-zinc-400">Nenhum aluno nas turmas</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {selectedClassIds.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-zinc-600 mb-1 flex items-center justify-between">
            <span>3. Responsáveis ({selectedGuardianIds.length}/{families.length})</span>
            <select value={gtypeFilter} onChange={(e) => setGtypeFilter(e.target.value)} className="text-[10px] border border-zinc-200 rounded px-1">
              <option value="">Todos</option>
              <option value="financial">Financeiro</option>
              <option value="pedagogical">Didático</option>
              <option value="both">Ambos</option>
            </select>
          </div>
          <div className="max-h-56 overflow-y-auto border border-zinc-200 rounded" data-testid="dnachat-fams-multi">
            {loading && <div className="p-3 text-center text-xs text-zinc-400">Carregando…</div>}
            {!loading && families.length === 0 && <div className="p-3 text-center text-xs text-zinc-400">Sem responsáveis nas turmas selecionadas</div>}
            {families.map((f) => (
              <label key={f.guardian_id} className="flex items-start gap-2 px-2 py-1.5 text-xs hover:bg-emerald-50 cursor-pointer border-b border-zinc-100" data-testid={`dnachat-fam-multi-${f.guardian_id.slice(0, 8)}`}>
                <input type="checkbox" className="mt-0.5" checked={selectedGuardianIds.includes(f.guardian_id)} onChange={() => toggleGuardian(f.guardian_id)} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-800 truncate flex items-center gap-1">
                    {f.guardian_name}
                    {f.whatsapp_available && <span className="text-[9px] px-1 rounded bg-emerald-100 text-emerald-700 font-bold">WA</span>}
                  </div>
                  {(f.students || []).length > 0 && (
                    <div className="text-[10px] text-zinc-600">
                      {(f.students || []).slice(0, 2).map((s) => `${s.student_name}·${s.class_name || '?'}`).join(' | ')}
                      {(f.students || []).length > 2 && ` +${f.students.length - 2}`}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-zinc-500">{f.phone || '—'}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {selectedGuardianIds.length > 0 && (
        <>
          <div className="rounded bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-700 space-y-0.5">
            <div><b>Turmas:</b> {selectedClassIds.length} · <b>Responsáveis:</b> {selectedFamilies.length} · <b>WhatsApps válidos:</b> <b className="text-emerald-700">{withWA.length}</b> · sem: {selectedFamilies.length - withWA.length}</div>
          </div>
          <textarea className="w-full border border-zinc-200 rounded px-2 py-1 text-sm" rows={2}
            placeholder="Digite mensagem opcional (deixe vazio para apenas iniciar as conversas)…"
            value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="dnachat-multi-msg" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={iniciarConversas} disabled={bulkBusy || selectedGuardianIds.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="dnachat-multi-init">
              Iniciar conversas ({selectedGuardianIds.length})
            </Button>
            <Button onClick={enviarMensagem} disabled={bulkBusy || !bulkText.trim() || withWA.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dnachat-multi-send">
              Enviar via WhatsApp ({withWA.length})
            </Button>
          </DialogFooter>
        </>
      )}
    </div>
  );
}


// ────────────────────────  DIALOG DE CHAMADO  ────────────────────────
function TicketsPanel({ open, onClose }) {
  const [tickets, setTickets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [category, setCategory] = useState('secretaria');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('media');

  const load = () => api.get('/dnachat/tickets').then((r) => setTickets(r.data?.tickets || [])).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!subject.trim() || !description.trim()) return toast.error('Preencha assunto e descrição');
    try {
      await api.post('/dnachat/tickets', { category, subject, description, priority });
      toast.success('Chamado aberto');
      setShowNew(false); setSubject(''); setDescription('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dnachat-tickets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5" />Chamados</DialogTitle>
        </DialogHeader>
        {!showNew ? (
          <>
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setShowNew(true)} className="bg-indigo-600 text-white" data-testid="dnachat-ticket-new-btn">
                <Plus className="w-4 h-4 mr-1" />Novo chamado
              </Button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {tickets.map((t) => (
                <div key={t.id} className="border border-zinc-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{t.subject}</div>
                    <Badge variant={t.status === 'aberto' ? 'default' : 'secondary'}>{t.status}</Badge>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    <Badge variant="outline" className="mr-1 uppercase">{t.category}</Badge>
                    prioridade {t.priority} · {fmtTime(t.created_at)}
                  </div>
                  <div className="text-sm text-zinc-700 mt-2">{t.description}</div>
                </div>
              ))}
              {tickets.length === 0 && <div className="text-center text-xs text-zinc-400 py-6">Nenhum chamado</div>}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-semibold">Categoria</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-zinc-200 rounded px-2 py-1.5 text-sm" data-testid="dnachat-ticket-category">
                  <option value="secretaria">Secretaria</option>
                  <option value="coordenacao">Coordenação</option>
                  <option value="financeiro">Financeiro</option>
                  <option value="pedagogico">Pedagógico</option>
                  <option value="tecnologia">Tecnologia</option>
                  <option value="direcao">Direção</option>
                </select>
              </div>
              <Input placeholder="Assunto" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="dnachat-ticket-subject" />
              <Textarea placeholder="Descreva sua solicitação…" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="dnachat-ticket-description" />
              <div>
                <label className="text-xs font-semibold">Prioridade</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border border-zinc-200 rounded px-2 py-1.5 text-sm">
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={create} className="bg-indigo-600 text-white" data-testid="dnachat-ticket-submit">Abrir chamado</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────  IA CHAT  ────────────────────────
function AIPanel({ open, onClose }) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const ask = async () => {
    if (!q.trim()) return;
    const question = q.trim();
    setMessages((m) => [...m, { role: 'user', text: question }]);
    setQ(''); setLoading(true);
    try {
      const { data } = await api.post('/dnachat/ai/ask', { question });
      setMessages((m) => [...m, { role: 'ai', text: data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'IA indisponível');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col" data-testid="dnachat-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-purple-700" />DNA IA · Assistente</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500">Consulta dados reais do DNACore. Pergunte sobre notas, financeiro, calendário, eventos.</div>
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {messages.length === 0 && (
            <div className="text-center text-xs text-zinc-400 mt-6">
              <Bot className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              Faça uma pergunta:<br />
              <span className="italic">{'"Quais meus títulos pendentes?"'}</span>,<br />
              <span className="italic">{'"Quais os próximos eventos?"'}</span>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-purple-50 border border-purple-200 text-zinc-800'}`}>
                {m.role === 'ai' && <div className="text-[10px] font-semibold text-purple-700 mb-0.5">DNA IA</div>}
                <div className="whitespace-pre-wrap">{m.text}</div>
              </div>
            </div>
          ))}
          {loading && <div className="text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /><span className="text-xs">Pensando…</span></div>}
          <div ref={endRef} />
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
          <Textarea
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pergunte à IA…"
            rows={1}
            className="resize-none"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
            data-testid="dnachat-ai-input"
          />
          <Button onClick={ask} disabled={loading || !q.trim()} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="dnachat-ai-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────  PAINEL PRINCIPAL  ────────────────────────
// V4.64 · Bipe web-audio (sem asset externo) para notificar novas mensagens
function playChatBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.34);
    o.onended = () => { try { ctx.close(); } catch (_) { /* noop */ } };
  } catch (_) { /* noop */ }
}

export default function DnaChatApp() {
  return (
    <DnaChatErrorBoundary>
      <DnaChatAppInner />
    </DnaChatErrorBoundary>
  );
}

// V4.77 · ErrorBoundary local — protege DNAChat de tela branca. Se qualquer subárvore
// crashar, mostra fallback ("Não foi possível carregar esta conversa.") com ações
// [Tentar novamente] e [Voltar para conversas]. Não silencia — loga [DNACHAT_RENDER_ERROR].
class DnaChatErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, errorInfo: null, retryKey: 0 }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error('[DNACHAT_RENDER_ERROR]', {
      component: 'DnaChatApp',
      message: (error && error.message) || String(error),
      stack: (error && error.stack) ? String(error.stack).split('\n').slice(0, 6).join('\n') : undefined,
    });
  }
  render() {
    if (this.state.error) {
      return (
        <div className="h-[calc(100vh-80px)] flex items-center justify-center bg-white rounded-lg border border-zinc-200" data-testid="dnachat-fallback">
          <div className="max-w-md text-center p-6">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-2xl">!</div>
            <div className="text-lg font-semibold text-zinc-800 mb-1">Não foi possível carregar esta conversa.</div>
            <div className="text-xs text-zinc-500 mb-4">{this.state.error?.message || 'Erro inesperado ao renderizar o chat.'}</div>
            <div className="flex gap-2 justify-center">
              <Button
                variant="outline"
                onClick={() => this.setState({ error: null, errorInfo: null, retryKey: this.state.retryKey + 1 })}
                data-testid="dnachat-fallback-retry"
              >Tentar novamente</Button>
              <Button
                className="bg-indigo-600 text-white"
                onClick={() => { try { window.location.assign('/v2/chat'); } catch (_) { window.location.reload(); } }}
                data-testid="dnachat-fallback-home"
              >Voltar para conversas</Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function DnaChatAppInner() {
  const [me, setMe] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('chats');
  const [newDialogType, setNewDialogType] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // V4.78 · Filtros server-side + lista de turmas para o multi-select
  const [filters, setFilters] = useState({ search: '', class_ids: [], guardian_type: undefined, channel: undefined, status: undefined });
  const [classList, setClassList] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 300);
    return () => clearTimeout(t);
  }, [filters.search]);
  useEffect(() => {
    api.get('/dnachat/broadcast/classes').then((r) => setClassList(r.data?.classes || [])).catch(() => setClassList([]));
  }, []);
  const clearFilters = () => setFilters({ search: '', class_ids: [], guardian_type: undefined, channel: undefined, status: undefined });
  // V4.80 · Modo seleção múltipla + bulk archive/restore
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Ao mudar filtros/aba durante seleção, limpar seleção para evitar ação em itens invisíveis
  useEffect(() => { if (selectionMode) setSelectedIds(new Set()); /* eslint-disable-next-line */ }, [filters.search, filters.class_ids, filters.channel, filters.status, filters.guardian_type]);
  const bulkArchive = async (ids) => {
    if (!ids || ids.length === 0) return;
    if (!window.confirm(`Arquivar ${ids.length} conversa${ids.length === 1 ? '' : 's'} selecionada${ids.length === 1 ? '' : 's'}?\n\nAs mensagens e anexos serão preservados.`)) return;
    try {
      const { data } = await api.post('/dnachat/conversations/bulk-archive', { conversation_ids: ids });
      toast.success(`${data?.archived || 0} conversa${(data?.archived || 0) === 1 ? '' : 's'} arquivada${(data?.archived || 0) === 1 ? '' : 's'}${data?.failed ? ` · ${data.failed} negada(s)` : ''}.`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao arquivar conversas.');
    }
  };
  const bulkRestore = async (ids) => {
    if (!ids || ids.length === 0) return;
    if (!window.confirm(`Restaurar ${ids.length} conversa${ids.length === 1 ? '' : 's'} arquivada${ids.length === 1 ? '' : 's'}?`)) return;
    try {
      const { data } = await api.post('/dnachat/conversations/bulk-restore', { conversation_ids: ids });
      toast.success(`${data?.restored || 0} conversa${(data?.restored || 0) === 1 ? '' : 's'} restaurada${(data?.restored || 0) === 1 ? '' : 's'}.`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao restaurar conversas.');
    }
  };
  // V4.64 · Guarda snapshot anterior para detectar incremento de unread_count
  const unreadMapRef = useRef({});
  const firstLoadRef = useRef(true);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    setMe(u);
  }, []);

  const reload = async () => {
    try {
      const f = filtersRef.current || {};
      const params = {};
      if ((f.search || '').trim()) params.search = f.search.trim();
      if ((f.class_ids || []).length) params.class_ids = f.class_ids.join(',');
      if (f.guardian_type) params.guardian_type = f.guardian_type;
      if (f.channel) params.channel = f.channel;
      // V4.80 · Status = archived usa include_archived e filtra local
      const wantsArchived = f.status === 'archived';
      if (wantsArchived) { params.include_archived = true; }
      else if (f.status) params.status = f.status;
      const { data } = await api.get('/dnachat/conversations', { params });
      let list = data.conversations || [];
      if (wantsArchived) list = list.filter((c) => !!c.archived_at);
      // V4.64 · Detecta conversas com unread_count aumentado desde a última consulta
      if (!firstLoadRef.current) {
        const bumps = [];
        for (const c of list) {
          const prev = unreadMapRef.current[c.id] || 0;
          const now = c.unread ?? c.unread_count ?? 0;
          if (now > prev) bumps.push({ conv: c, delta: now - prev });
        }
        if (bumps.length > 0) {
          playChatBeep();
          bumps.slice(0, 3).forEach((b) => {
            const who = b.conv.display_name || b.conv.name || 'contato';
            toast.info(`Nova mensagem de ${who}${b.delta > 1 ? ` (+${b.delta})` : ''}`, {
              duration: 6000,
              action: {
                label: 'Abrir',
                onClick: () => setSelected(b.conv),
              },
            });
          });
        }
      }
      unreadMapRef.current = Object.fromEntries(list.map((c) => [c.id, c.unread ?? c.unread_count ?? 0]));
      firstLoadRef.current = false;
      setConversations(list);
    } catch { /* ignore */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debouncedSearch, filters.class_ids, filters.guardian_type, filters.channel, filters.status]);
  useEffect(() => { const t = setInterval(reload, 8000); return () => clearInterval(t); }, []);

  // V4.68 · Arquivar / Excluir conversa
  const archiveConv = async (conv) => {
    try {
      await api.post(`/dnachat/conversations/${conv.id}/archive`);
      toast.success(`Conversa arquivada${conv.display_name ? ' · ' + conv.display_name : ''}`);
      if (selected?.id === conv.id) setSelected(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao arquivar');
    }
  };
  const deleteConv = async (conv) => {
    const ok = window.confirm(
      `Excluir esta conversa do DNAChat?\n\nResponsável: ${conv.display_name || conv.name || '—'}\nTelefone: ${conv.phone || conv.guardian_phone || '—'}\n\nO histórico continua preservado (soft delete). Uma nova mensagem do mesmo número REABRIRÁ automaticamente.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/dnachat/conversations/${conv.id}`);
      toast.success(`Conversa excluída · ${data?.messages_preserved || 0} mensagens preservadas`);
      if (selected?.id === conv.id) setSelected(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao excluir');
    }
  };

  return (
    <div className="h-[calc(100vh-80px)] flex bg-white rounded-lg overflow-hidden border border-zinc-200" data-testid="dnachat-root">
      <ConversationsSidebar
        me={me}
        tab={tab}
        setTab={setTab}
        conversations={conversations}
        selected={selected}
        onSelect={setSelected}
        onNewChat={() => setNewDialogType('direct')}
        onNewGroup={() => setNewDialogType('group')}
        onNewBroadcast={() => setBroadcastOpen(true)}
        onOpenTickets={() => setTicketsOpen(true)}
        onOpenAI={() => setAiOpen(true)}
        onArchive={archiveConv}
        onDelete={deleteConv}
        filters={filters}
        setFilters={setFilters}
        classList={classList}
        onClearFilters={clearFilters}
        selectionMode={selectionMode}
        setSelectionMode={setSelectionMode}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onBulkArchive={bulkArchive}
        onBulkRestore={bulkRestore}
      />
      <ChatArea me={me} conv={selected} onReload={reload} />
      <NewConversationDialog
        open={!!newDialogType}
        onClose={() => setNewDialogType(null)}
        type={newDialogType || 'direct'}
        onCreated={(conv) => { reload(); setSelected(conv); }}
      />
      <BroadcastDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        onCreated={() => { reload(); }}
      />
      <TicketsPanel open={ticketsOpen} onClose={() => setTicketsOpen(false)} />
      <AIPanel open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
